import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  Switch,
  Row,
  Col,
  Space,
  Button,
  Spin,
  Typography,
  message,
  Tabs,
  Modal,
} from 'antd';
import type { UploadFile } from 'antd/es/upload';
import { DeleteOutlined, PlusOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams, useLocation, useOutletContext } from 'react-router-dom';
import { Alert } from 'flowbite-react';
import dayjs, { Dayjs } from 'dayjs';
import { businessService, CreateBusinessData } from '../services/businesses';
import { customerService } from '../services/customers';
import api from '../services/api';
import customerApi from '../services/customerApi';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { BusinessOverviewTab } from '../components/BusinessForm/OverviewTab';
import { BusinessEngagementTab } from '../components/BusinessForm/EngagementTab';
import { BusinessAddressTab, CreateBusinessAddressData } from '../components/BusinessForm/AddressTab';
import { BusinessNatureTab } from '../components/BusinessForm/NatureTab';
import { BusinessFinancialTab, CreateBusinessFinancialData } from '../components/BusinessForm/FinancialTab';
import { BusinessEmployersTab, CreateBusinessEmployerData } from '../components/BusinessForm/EmployersTab';
import { BusinessDirectorsTab, CreateBusinessDirectorData } from '../components/BusinessForm/DirectorsTab';
import { BusinessMembersTab, CreateBusinessMemberData } from '../components/BusinessForm/MembersTab';
import { BusinessTimelineTab } from '../components/BusinessForm/TimelineTab';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

type LayoutHeaderContext = {
  setHeaderContent: (content: React.ReactNode) => void;
};

interface User {
  id: number;
  name: string;
  email: string;
}

interface BusinessFormPageProps {
  isCustomerPortalPage?: boolean;
}

export const BusinessFormPage: React.FC<BusinessFormPageProps> = ({ isCustomerPortalPage = false }) => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm<CreateBusinessData>();
  const outletContext = useOutletContext<LayoutHeaderContext | null>();
  const setHeaderContent = outletContext?.setHeaderContent;
  const { user } = useAuth();
  const canViewDetails = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.details.view');
  const canUpdateDetails = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.details.update');
  const canCreateDetails = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.details.create');
  const canViewAddress = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.address.view');
  const canUpdateAddress = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.address.update');
  const canCreateAddress = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.address.create');
  const canViewNature = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.nature.view');
  const canUpdateNature = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.nature.update');
  const canCreateNature = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.nature.create');
  const canViewFinancial = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.financial.view');
  const canUpdateFinancial = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.financial.update');
  const canCreateFinancial = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.financial.create');
  const canViewEmployers = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.employers.view');
  const canUpdateEmployers = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.employers.update');
  const canCreateEmployers = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.employers.create');
  const canViewEngagement = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.engagement.view');
  const canUpdateEngagement = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.engagement.update');
  const canCreateEngagement = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.engagement.create');
  const canViewDirectors = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.directors.view');
  const canUpdateDirectors = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.directors.update');
  const canCreateDirectors = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.directors.create');
  const canViewMembers = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.members.view');
  const canUpdateMembers = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.members.update');
  const canCreateMembers = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.members.create');

  useEffect(() => {
    if (!setHeaderContent) {
      return;
    }
    setHeaderContent(
      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
        Businesses
      </span>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);
  const canViewTimeline = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.timeline.view');
  const canCreateTimeline = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.timeline.create');
  const canViewCompanyChop = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.company-chop.view');
  const canUpdateCompanyChop = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.company-chop.update');
  const canCreateCompanyChop = isCustomerPortalPage ? true : hasPermission(user, 'customer.business.company-chop.create');
  const canVerifyRequests = isCustomerPortalPage ? false : hasPermission(user, 'customer.business.verify');
  const canSeeDetailsTab = isEdit ? canViewDetails : canCreateDetails;
  const canSeeAddressTab = isEdit ? canViewAddress : canCreateAddress;
  const canSeeNatureTab = isEdit ? canViewNature : canCreateNature;
  const canSeeFinancialTab = isEdit ? canViewFinancial : canCreateFinancial;
  const canSeeEmployersTab = isEdit ? canViewEmployers : canCreateEmployers;
  const canSeeEngagementTab = isEdit ? canViewEngagement : canCreateEngagement;
  const canSeeDirectorsTab = isEdit ? canViewDirectors : canCreateDirectors;
  const canSeeMembersTab = isEdit ? canViewMembers : canCreateMembers;
  const canSeeTimelineTab = isEdit ? canViewTimeline : canCreateTimeline;
  const canSeeCompanyChop = isEdit ? canViewCompanyChop : canCreateCompanyChop;
  const canEditDetailsFields = isEdit ? canUpdateDetails : canCreateDetails;
  const canEditAddressFields = isEdit ? canUpdateAddress : canCreateAddress;
  const canEditNatureFields = isEdit ? canUpdateNature : canCreateNature;
  const canEditFinancialFields = isEdit ? canUpdateFinancial : canCreateFinancial;
  const canEditEmployersFields = isEdit ? canUpdateEmployers : canCreateEmployers;
  const canEditEngagementFields = isEdit ? canUpdateEngagement : canCreateEngagement;
  const canEditDirectorsFields = isEdit ? canUpdateDirectors : canCreateDirectors;
  const canEditMembersFields = isEdit ? canUpdateMembers : canCreateMembers;
  const canEditCompanyChopFields = isEdit ? canUpdateCompanyChop : canCreateCompanyChop;
  const canDelete = isCustomerPortalPage ? false : hasPermission(user, 'customer.business.delete');
  const canEnterEditMode =
    canUpdateDetails
    || canUpdateAddress
    || canUpdateNature
    || canUpdateFinancial
    || canUpdateEmployers
    || canUpdateEngagement
    || canUpdateDirectors
    || canUpdateMembers
    || canUpdateCompanyChop;
  const getDefaultTabKey = () => {
    if (canSeeDetailsTab) return 'overview';
    if (canSeeEngagementTab) return 'engagement';
    if (canSeeAddressTab) return 'address';
    if (canSeeNatureTab) return 'nature';
    if (canSeeFinancialTab) return 'financial';
    if (canSeeEmployersTab) return 'employers';
    if (canSeeDirectorsTab) return 'directors';
    if (canSeeMembersTab) return 'members';
    if (canSeeTimelineTab) return 'timeline';
    return 'overview';
  };
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || getDefaultTabKey());
  const [isEditing, setIsEditing] = useState<boolean>(!isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [rejectAllModalOpen, setRejectAllModalOpen] = useState(false);
  const [loadingBusiness, setLoadingBusiness] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [staff, setStaff] = useState<User[]>([]);
  const [addresses, setAddresses] = useState<CreateBusinessAddressData[]>([]);
  const [msicCodes, setMsicCodes] = useState<Array<{ code: string; description: string }>>([]);
  const [originalMsicCodes, setOriginalMsicCodes] = useState<Array<{ code: string; description: string }>>([]);
  const [financials, setFinancials] = useState<CreateBusinessFinancialData[]>([]);
  const [originalFinancials, setOriginalFinancials] = useState<CreateBusinessFinancialData[]>([]);
  const [employer, setEmployer] = useState<CreateBusinessEmployerData>({
    employer_no: '',
    employer_no_password: '',
    employer_account_no: '',
    sst_id: '',
    sst_password: '',
    sst_account_no: '',
    kwsp_id: '',
    kwsp_password: '',
    kwsp_account_no: '',
    perkeso_id: '',
    perkeso_password: '',
    perkeso_account_no: '',
    hrdc_id: '',
    hrdc_password: '',
    hrdc_account_no: '',
    pcb: '',
  });
  const [directors, setDirectors] = useState<CreateBusinessDirectorData[]>([]);
  const [originalDirectors, setOriginalDirectors] = useState<CreateBusinessDirectorData[]>([]);
  const [members, setMembers] = useState<CreateBusinessMemberData[]>([]);
  const [originalMembers, setOriginalMembers] = useState<CreateBusinessMemberData[]>([]);
  const [changeRequests, setChangeRequests] = useState<any[]>([]);
  const [pendingChanges, setPendingChanges] = useState<Record<string, string>>({});
  const [pendingApprovals, setPendingApprovals] = useState<number[]>([]); // Change request IDs to approve
  const [pendingRejections, setPendingRejections] = useState<number[]>([]); // Change request IDs to reject
  const [addressPendingChanges, setAddressPendingChanges] = useState<Record<string, string>>({});
  const [originalAddresses, setOriginalAddresses] = useState<CreateBusinessAddressData[]>([]);
  const [deletedAddresses, setDeletedAddresses] = useState<Array<{ index: number; data: any }>>([]);
  const [addressFiles, setAddressFiles] = useState<Record<string, UploadFile[]>>({});
  const [msicCodePendingChanges, setMsicCodePendingChanges] = useState<Record<string, string>>({});
  const [deletedMsicCodes, setDeletedMsicCodes] = useState<Array<{ index: number; data: any }>>([]);
  const [financialPendingChanges, setFinancialPendingChanges] = useState<Record<string, string>>({});
  const [deletedFinancials, setDeletedFinancials] = useState<Array<{ index: number; data: any }>>([]);
  const [directorPendingChanges, setDirectorPendingChanges] = useState<Record<string, string>>({});
  const [deletedDirectors, setDeletedDirectors] = useState<Array<{ index: number; data: any }>>([]);
  const [memberPendingChanges, setMemberPendingChanges] = useState<Record<string, string>>({});
  const [deletedMembers, setDeletedMembers] = useState<Array<{ index: number; data: any }>>([]);
  const [employerPendingChanges, setEmployerPendingChanges] = useState<Record<string, string>>({});
  const [businessName, setBusinessName] = useState<string>('');
  const [businessType, setBusinessType] = useState<'potential' | 'active' | 'inactive'>('potential');
  const [supportingFiles, setSupportingFiles] = useState<{
    name?: UploadFile[];
    sst_no?: UploadFile[];
    tin_no?: UploadFile[];
    trademark_logo?: UploadFile[];
    industry_specific_license?: UploadFile[];
    company_chop?: UploadFile[];
  }>({});
  const [removedSupportingFiles, setRemovedSupportingFiles] = useState<Record<string, boolean>>({});
  const [businesses, setBusinesses] = useState<Array<{ id: number; name: string }>>([]);
  const [engagementFiles, setEngagementFiles] = useState<{
    account_attachment?: UploadFile[];
    tax_attachment?: UploadFile[];
    audit_attachment?: UploadFile[];
    secretary_attachment?: UploadFile[];
  }>({});
  const [submitNotice, setSubmitNotice] = useState<{ type: 'success' | 'failure'; message: string } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isSeniorOrJunior = !!user?.roles?.some((role) => role === 'senior' || role === 'junior');
  const [originalOverviewValues, setOriginalOverviewValues] = useState({
    name: '',
    former_name: '',
    registration_no: '',
    entity: '',
    tin_no: '',
    incorporation_date: '',
    year_end: '',
    contact_number: '',
    email: '',
    supporting_name: '',
    supporting_sst_no: '',
    supporting_tin_no: '',
    supporting_company_chop: '',
  });
  const [originalEngagementValues, setOriginalEngagementValues] = useState({
    engagement: '',
    advisor_staff_id: '',
    advisor_appoint_date: '',
    advisor_resign_date: '',
    account_staff_id: '',
    account_appoint_date: '',
    account_resign_date: '',
    tax_staff_id: '',
    tax_appoint_date: '',
    tax_resign_date: '',
    audit_staff_id: '',
    audit_appoint_date: '',
    audit_resign_date: '',
    secretary_staff_id: '',
    secretary_appoint_date: '',
    secretary_resign_date: '',
  });
  const [latestBusinessRequest, setLatestBusinessRequest] = useState<any | null>(null);
  const [latestBusinessEngagementRequest, setLatestBusinessEngagementRequest] = useState<any | null>(null);
  const [latestBusinessNatureRequest, setLatestBusinessNatureRequest] = useState<any | null>(null);
  const [latestBusinessEmployerRequest, setLatestBusinessEmployerRequest] = useState<any | null>(null);
  const pendingChangeRequests = useMemo(
    () => changeRequests.filter((cr) => cr.status === 'pending'),
    [changeRequests]
  );
  const pendingBusinessRequestsCount = useMemo(
    () =>
      [latestBusinessRequest, latestBusinessEngagementRequest, latestBusinessNatureRequest, latestBusinessEmployerRequest]
        .filter(Boolean)
        .length,
    [latestBusinessRequest, latestBusinessEngagementRequest, latestBusinessNatureRequest, latestBusinessEmployerRequest]
  );
  const hasPendingAnyRequests = pendingChangeRequests.length > 0 || pendingBusinessRequestsCount > 0;
  const [originalEmployerValues, setOriginalEmployerValues] = useState<CreateBusinessEmployerData>({
    employer_no: '',
    employer_no_password: '',
    employer_account_no: '',
    sst_id: '',
    sst_password: '',
    sst_account_no: '',
    kwsp_id: '',
    kwsp_password: '',
    kwsp_account_no: '',
    perkeso_id: '',
    perkeso_password: '',
    perkeso_account_no: '',
    hrdc_id: '',
    hrdc_password: '',
    hrdc_account_no: '',
    pcb: '',
  });
  const [originalNatureValues, setOriginalNatureValues] = useState({
    business_description: '',
    trademark_logo: '',
    industry_specific_license: '',
  });
  const normalizeDateOnly = (val?: string | Dayjs | Date | null) => {
    if (!val) return '';
    if (typeof val === 'string') {
      return dayjs(val).startOf('day').format('YYYY-MM-DD');
    }
    if (dayjs.isDayjs(val)) {
      return val.startOf('day').format('YYYY-MM-DD');
    }
    return dayjs(val).startOf('day').format('YYYY-MM-DD');
  };
  const normalizeDirectorMemberDate = (
    field: keyof CreateBusinessDirectorData | keyof CreateBusinessMemberData,
    value: string | number | undefined
  ) => {
    if (field === 'appoint_date' || field === 'resign_date') {
      if (!value) return '';
      return normalizeDateOnly(value as unknown as string);
    }
    return value;
  };

  useEffect(() => {
    fetchLists();
    if (isEdit && id) {
      loadBusiness(parseInt(id, 10));
    } else {
      form.setFieldsValue({ type: 'potential' });
      setBusinessType('potential');
      setIsEditing(true); // For new businesses, enable editing by default
    }
  }, [isEdit, id, form]);

  useEffect(() => {
    const stateNotice = (location.state as { notice?: { type: 'success' | 'failure'; message: string } } | null)?.notice;
    if (stateNotice) {
      setSubmitNotice(stateNotice);
      navigate(location.pathname + location.search, { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
      return;
    }
    setActiveTab(getDefaultTabKey());
  }, [searchParams, canSeeDetailsTab, canSeeEngagementTab, canSeeAddressTab, canSeeNatureTab, canSeeFinancialTab, canSeeEmployersTab, canSeeDirectorsTab, canSeeMembersTab, canSeeTimelineTab]);

  const fetchLists = async () => {
    try {
      if (isCustomerPortalPage) {
        // Customer portal only needs customers list for Directors/Members dropdowns
        const [customersResponse, businessesResponse] = await Promise.all([
          customerService.getAll(),
          customerApi.get('/customer/businesses').catch(() => ({ data: { data: [] } })),
        ]);
        setCustomers(customersResponse.data || []);
        const businessesData = businessesResponse.data?.data || businessesResponse.data || [];
        setBusinesses(businessesData.map((business: any) => ({ id: business.id, name: business.name })));
      } else {
        const [customersResponse, usersResponse, businessesResponse] = await Promise.all([
          api.get('/customers/options').catch(() => ({ data: { data: [] } })),
          api.get('/users/options').catch(() => ({ data: { data: [] } })),
          businessService.getAll().catch(() => ({ data: [] })),
        ]);
        setCustomers(customersResponse.data?.data || customersResponse.data || []);
        setStaff(usersResponse.data?.data || usersResponse.data || []);
        const businessesData = businessesResponse.data || [];
        setBusinesses(businessesData.map((business: any) => ({ id: business.id, name: business.name })));
      }
    } catch {
      message.warning('Unable to load dropdown lists');
    }
  };

  const loadBusiness = async (businessId: number) => {
    setLoadingBusiness(true);
    try {
      const business = isCustomerPortalPage
        ? await customerApi.get(`/customer/businesses/${businessId}`).then(res => res.data?.data || res.data)
        : await businessService.getById(businessId);
      
      // Convert engagement string to array if needed
      let engagementValue: string | string[] | undefined = business.engagement;
      if (engagementValue && typeof engagementValue === 'string') {
        // If engagement is a comma-separated string, split it
        engagementValue = engagementValue.split(',').map(s => s.trim()).filter(s => s);
      } else if (!engagementValue) {
        engagementValue = [];
      }
      
      // Get first contact for single contact fields
      const firstContact = business.contacts && business.contacts.length > 0 ? business.contacts[0] : null;
      
      // Get first nature for single nature fields
      const firstNature = business.natures && business.natures.length > 0 ? business.natures[0] : null;
      
      setBusinessName(business.name);
      setBusinessType(business.type);
      if (isCustomerPortalPage) {
        const customerFromBusiness = (business as any).customer;
        const selfCustomer = customerFromBusiness
          ? { id: customerFromBusiness.id, name: customerFromBusiness.name }
          : { id: business.customer_id, name: '' };
        setCustomers([selfCustomer]);
        setStaff([]);
      }
      
      // Convert engagement date fields to dayjs objects
      const engagementDateFields = [
        'advisor_appoint_date',
        'advisor_resign_date',
        'account_appoint_date',
        'account_resign_date',
        'tax_appoint_date',
        'tax_resign_date',
        'audit_appoint_date',
        'audit_resign_date',
        'secretary_appoint_date',
        'secretary_resign_date',
      ];
      
      const supporting = business.supportings && business.supportings.length > 0 ? business.supportings[0] : null;
      const formerNameValue = supporting?.former_name_text || supporting?.former_name || '';
      const formValues: any = {
        ...business,
        engagement: engagementValue,
        incorporation_date: business.incorporation_date ? dayjs(business.incorporation_date) : undefined,
        contact_number: firstContact?.contact_number || '',
        email: firstContact?.email || '',
        business_description: firstNature?.business_description || '',
        trademark_logo: firstNature?.trademark_logo || '',
        industry_specific_license: firstNature?.industry_specific_license || '',
        former_name: formerNameValue,
      };
      
      // Convert all engagement date fields to dayjs objects
      engagementDateFields.forEach(field => {
        if (business[field as keyof typeof business]) {
          formValues[field] = dayjs(business[field as keyof typeof business] as string);
        } else {
          formValues[field] = undefined;
        }
      });
      
      form.setFieldsValue(formValues);
      
      // Load addresses from business.addresses (new structure)
      const addressList: CreateBusinessAddressData[] = [];
      if (business.addresses && business.addresses.length > 0) {
        business.addresses.forEach((address: any) => {
          addressList.push({
            id: address.id,
            type: address.type || 'main',
          address1: address.address || address.address1 || '',
          address2: '',
          attachment: address.attachment || undefined,
        } as CreateBusinessAddressData);
        });
      }
      // Fallback: also check contacts for backward compatibility
      if (addressList.length === 0 && business.contacts && business.contacts.length > 0) {
        business.contacts.forEach((contact: any) => {
          if (contact.main_address1 || contact.main_address2) {
            addressList.push({
              id: contact.id,
              type: 'main',
              address1: contact.main_address1 || '',
              address2: contact.main_address2 || '',
              _contactId: contact.id,
              _addressType: 'main',
            } as CreateBusinessAddressData);
          }
          if (contact.branch_address1 || contact.branch_address2) {
            addressList.push({
              id: contact.id,
              type: 'branch',
              address1: contact.branch_address1 || '',
              address2: contact.branch_address2 || '',
              _contactId: contact.id,
              _addressType: 'branch',
            } as CreateBusinessAddressData);
          }
        });
      }
      const addressFilesData: Record<string, UploadFile[]> = {};
      addressList.forEach((address, index) => {
        if (address.attachment) {
          const filename = (address.attachment as string).split('/').pop() || 'attachment';
          addressFilesData[`address_${index}`] = [{
            uid: `-address-${index}`,
            name: filename,
            status: 'done',
          } as UploadFile];
        }
      });
      setAddressFiles(addressFilesData);
      setAddresses(addressList);
      setOriginalAddresses(addressList);
      // Load MSIC codes from natures (extract all MSIC codes from all natures, max 5)
      const msicCodeList: Array<{ code: string; description: string }> = [];
      if (business.natures && business.natures.length > 0) {
        business.natures.forEach((nature: any) => {
          const codes = [
            { code: nature.msic_code_1, description: nature.msic_description_1 || '' },
            { code: nature.msic_code_2, description: nature.msic_description_2 || '' },
            { code: nature.msic_code_3, description: nature.msic_description_3 || '' },
            { code: nature.msic_code_4, description: nature.msic_description_4 || '' },
            { code: nature.msic_code_5, description: nature.msic_description_5 || '' },
          ];
          codes.forEach((item) => {
            if (item.code && msicCodeList.length < 5) {
              msicCodeList.push({
                code: item.code,
                description: item.description,
              });
            }
          });
        });
      }
      setMsicCodes(msicCodeList);
      setOriginalMsicCodes(msicCodeList);
      setFinancials((business.financials || []).map((fin: any) => ({
        id: fin.id,
        type: fin.type || '',
        bank: fin.bank || '',
        branch_name: fin.branch_name || '',
        bank_account: fin.bank_account || '',
      })));
      setOriginalFinancials((business.financials || []).map((fin: any) => ({
        id: fin.id,
        type: fin.type || '',
        bank: fin.bank || '',
        branch_name: fin.branch_name || '',
        bank_account: fin.bank_account || '',
      })));
      setDirectors((business.directors || []).map((dir: any) => ({
        id: dir.id,
        customer_id: dir.customer_id || undefined,
        name: dir.name || '',
        identification_no: dir.identification_no || '',
        appoint_date: dir.appoint_date ? normalizeDateOnly(dir.appoint_date) : '',
        resign_date: dir.resign_date ? normalizeDateOnly(dir.resign_date) : '',
        position: dir.position || '',
      })));
      setOriginalDirectors((business.directors || []).map((dir: any) => ({
        id: dir.id,
        customer_id: dir.customer_id || undefined,
        name: dir.name || '',
        identification_no: dir.identification_no || '',
        appoint_date: dir.appoint_date ? normalizeDateOnly(dir.appoint_date) : '',
        resign_date: dir.resign_date ? normalizeDateOnly(dir.resign_date) : '',
        position: dir.position || '',
      })));
      // Load first employer (only one employer allowed)
      const firstEmployer = business.employers && business.employers.length > 0 ? business.employers[0] : null;
      setEmployer(firstEmployer || {
        employer_no: '',
        employer_no_password: '',
        employer_account_no: '',
        sst_id: '',
        sst_password: '',
        sst_account_no: '',
        kwsp_id: '',
        kwsp_password: '',
        kwsp_account_no: '',
        perkeso_id: '',
        perkeso_password: '',
        perkeso_account_no: '',
        hrdc_id: '',
        hrdc_password: '',
        hrdc_account_no: '',
        pcb: '',
      });
      setOriginalEmployerValues({
        employer_no: firstEmployer?.employer_no || '',
        employer_no_password: (firstEmployer as any)?.employer_no_password || '',
        employer_account_no: (firstEmployer as any)?.employer_account_no || '',
        sst_id: firstEmployer?.sst_id || '',
        sst_password: (firstEmployer as any)?.sst_password || '',
        sst_account_no: (firstEmployer as any)?.sst_account_no || '',
        kwsp_id: firstEmployer?.kwsp_id || '',
        kwsp_password: (firstEmployer as any)?.kwsp_password || '',
        kwsp_account_no: (firstEmployer as any)?.kwsp_account_no || '',
        perkeso_id: firstEmployer?.perkeso_id || '',
        perkeso_password: (firstEmployer as any)?.perkeso_password || '',
        perkeso_account_no: (firstEmployer as any)?.perkeso_account_no || '',
        hrdc_id: firstEmployer?.hrdc_id || '',
        hrdc_password: (firstEmployer as any)?.hrdc_password || '',
        hrdc_account_no: (firstEmployer as any)?.hrdc_account_no || '',
        pcb: (firstEmployer as any)?.pcb || '',
      });
      setMembers((business.members || []).map((mem: any) => ({
        id: mem.id,
        type: mem.type || (mem.customer_id ? 'customer' : undefined),
        reference_id: mem.reference_id || mem.customer_id || undefined,
        customer_id: mem.customer_id || undefined,
        name: mem.name || '',
        identification_no: mem.identification_no || '',
        appoint_date: mem.appoint_date ? normalizeDateOnly(mem.appoint_date) : '',
        resign_date: mem.resign_date ? normalizeDateOnly(mem.resign_date) : '',
        share: mem.share || 0,
      })));
      setOriginalMembers((business.members || []).map((mem: any) => ({
        id: mem.id,
        type: mem.type || (mem.customer_id ? 'customer' : undefined),
        reference_id: mem.reference_id || mem.customer_id || undefined,
        customer_id: mem.customer_id || undefined,
        name: mem.name || '',
        identification_no: mem.identification_no || '',
        appoint_date: mem.appoint_date ? normalizeDateOnly(mem.appoint_date) : '',
        resign_date: mem.resign_date ? normalizeDateOnly(mem.resign_date) : '',
        share: mem.share || 0,
      })));
      
      // Load supporting files (name, sst_no, tin_no, trademark_logo, industry_specific_license, company_chop)
      const files: typeof supportingFiles = {};
      if (supporting) {
        const fileFields = ['name', 'sst_no', 'tin_no', 'trademark_logo', 'industry_specific_license', 'company_chop'] as const;
        fileFields.forEach(field => {
          const value = (supporting as any)[field];
          if (value) {
            const s3Path = value;
            if (s3Path && typeof s3Path === 'string') {
              const filename = s3Path.split('/').pop() || field;
              files[field] = [{
                uid: `-${field}`,
                name: filename,
                status: 'done',
                url: s3Path, // Store raw S3 path
              } as UploadFile];
            }
          }
        });
      }
      setSupportingFiles(files);
      setRemovedSupportingFiles({});
      
      // Load engagement files (account_attachment, tax_attachment, audit_attachment, secretary_attachment)
      const engagementFileFields = ['account_attachment', 'tax_attachment', 'audit_attachment', 'secretary_attachment'] as const;
      const engagementFileData: typeof engagementFiles = {};
      engagementFileFields.forEach(field => {
        const value = business[field as keyof typeof business];
        if (value) {
          const s3Path = value as string;
          if (s3Path && typeof s3Path === 'string') {
            const filename = s3Path.split('/').pop() || field;
            engagementFileData[field] = [{
              uid: `-${field}`,
              name: filename,
              status: 'done',
              url: s3Path, // Store raw S3 path
            } as UploadFile];
          }
        }
      });
      setEngagementFiles(engagementFileData);
      
      setChangeRequests((business as any).change_requests || (business as any).changeRequests || []);
      setLatestBusinessRequest((business as any).latest_business_request || null);
      setLatestBusinessEngagementRequest((business as any).latest_business_engagement_request || null);
      setLatestBusinessNatureRequest((business as any).latest_business_nature_request || null);
      setLatestBusinessEmployerRequest((business as any).latest_business_employer_request || null);
      setOriginalOverviewValues({
        name: business.name || '',
        former_name: formerNameValue || '',
        registration_no: business.registration_no || '',
        entity: business.entity || '',
        tin_no: business.tin_no || '',
        incorporation_date: business.incorporation_date || '',
        year_end: business.year_end || '',
        contact_number: firstContact?.contact_number || '',
        email: firstContact?.email || '',
        supporting_name: supporting?.name || '',
        supporting_sst_no: supporting?.sst_no || '',
        supporting_tin_no: supporting?.tin_no || '',
        supporting_company_chop: supporting?.company_chop || '',
      });
      setOriginalEngagementValues({
        engagement: business.engagement || '',
        advisor_staff_id: business.advisor_staff_id ? String(business.advisor_staff_id) : '',
        advisor_appoint_date: business.advisor_appoint_date || '',
        advisor_resign_date: business.advisor_resign_date || '',
        account_staff_id: business.account_staff_id ? String(business.account_staff_id) : '',
        account_appoint_date: business.account_appoint_date || '',
        account_resign_date: business.account_resign_date || '',
        tax_staff_id: business.tax_staff_id ? String(business.tax_staff_id) : '',
        tax_appoint_date: business.tax_appoint_date || '',
        tax_resign_date: business.tax_resign_date || '',
        audit_staff_id: business.audit_staff_id ? String(business.audit_staff_id) : '',
        audit_appoint_date: business.audit_appoint_date || '',
        audit_resign_date: business.audit_resign_date || '',
        secretary_staff_id: business.secretary_staff_id ? String(business.secretary_staff_id) : '',
        secretary_appoint_date: business.secretary_appoint_date || '',
        secretary_resign_date: business.secretary_resign_date || '',
      });
      setOriginalNatureValues({
        business_description: firstNature?.business_description || '',
        trademark_logo: firstNature?.trademark_logo || '',
        industry_specific_license: firstNature?.industry_specific_license || '',
      });
      setIsEditing(false);
    } catch {
      message.error('Failed to load business');
      navigate(isCustomerPortalPage ? '/customer/personal' : '/businesses');
    } finally {
      setLoadingBusiness(false);
    }
  };

  // Console log change requests by tab
  useEffect(() => {
    if (changeRequests.length === 0) {
      console.log('📋 Business Change Requests: No pending requests');
      return;
    }

    const groupedByTab: Record<string, any[]> = {
      overview: [],
      address: [],
      nature: [],
      financial: [],
      director: [],
      member: [],
      employer: [],
    };

    changeRequests.forEach(cr => {
      const fieldName = cr.field_name || '';
      
      // Overview tab: fields that don't match other patterns
      if (
        !fieldName.startsWith('msic_') &&
        !fieldName.startsWith('address_') &&
        !fieldName.startsWith('financial_') &&
        !fieldName.startsWith('director_') &&
        !fieldName.startsWith('member_') &&
        !fieldName.startsWith('employer_') &&
        !fieldName.startsWith('contact_')
      ) {
        groupedByTab.overview.push(cr);
      }
      // Address tab
      else if (fieldName.startsWith('address_')) {
        groupedByTab.address.push(cr);
      }
      // Nature tab (MSIC Codes)
      else if (fieldName.startsWith('msic_')) {
        groupedByTab.nature.push(cr);
      }
      // Financial tab
      else if (fieldName.startsWith('financial_')) {
        groupedByTab.financial.push(cr);
      }
      // Directors tab
      else if (fieldName.startsWith('director_')) {
        groupedByTab.director.push(cr);
      }
      // Members tab
      else if (fieldName.startsWith('member_')) {
        groupedByTab.member.push(cr);
      }
      // Employers tab
      else if (fieldName.startsWith('employer_')) {
        groupedByTab.employer.push(cr);
      }
      // Contact fields (address related, but shown in overview)
      else if (fieldName.startsWith('contact_')) {
        groupedByTab.overview.push(cr);
      }
    });

    console.log('📋 Business Change Requests by Tab:');
    Object.entries(groupedByTab).forEach(([tab, requests]) => {
      if (requests.length > 0) {
        console.log(`  ${tab.toUpperCase()}: ${requests.length} request(s)`, requests.map(r => ({
          id: r.id,
          field: r.field_name,
          status: r.status,
          old_value: r.old_value,
          new_value: r.new_value,
        })));
      }
    });
  }, [changeRequests]);

  // Calculate business type based on engagement dates
  const calculateBusinessType = (): 'potential' | 'active' | 'inactive' => {
    const allValues = form.getFieldsValue() as any;
    
    // Only advisor row controls status.
    const advisorResignDate = allValues.advisor_resign_date;
    const advisorAppointDate = allValues.advisor_appoint_date;
    const hasAnyResignDate = advisorResignDate !== null && advisorResignDate !== undefined && advisorResignDate !== '';
    const hasAnyAppointDate = advisorAppointDate !== null && advisorAppointDate !== undefined && advisorAppointDate !== '';
    
    // If any row has resign date, type is inactive
    if (hasAnyResignDate) {
      return 'inactive';
    }
    
    // If any row has appoint date, type is active
    if (hasAnyAppointDate) {
      return 'active';
    }
    
    // If no appoint dates, type is potential
    return 'potential';
  };

  const handleEngagementDateChange = () => {
    const newType = calculateBusinessType();
    form.setFieldsValue({ type: newType });
    setBusinessType(newType);
  };

  const handleDelete = async () => {
    if (!id) return;
    if (isCustomerPortalPage) {
      message.info('Delete is not available in customer portal');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this business?')) return;
    
    try {
      await businessService.delete(parseInt(id, 10));
      message.success('Business deleted');
      navigate('/businesses');
    } catch {
      message.error('Failed to delete business');
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      if (isEdit && id && isSeniorOrJunior && activeTab === 'overview' && !isCustomerPortalPage) {
        const normalizeValue = (value: any) => {
          if (value === undefined || value === null) return '';
          return String(value);
        };
        const normalizeDate = (value: any) => {
          if (!value) return '';
          if (dayjs.isDayjs(value)) return value.format('YYYY-MM-DD');
          if (typeof value === 'string') {
            const parsed = dayjs(value, ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
            if (parsed.isValid()) return parsed.format('YYYY-MM-DD');
            const fallback = dayjs(value);
            return fallback.isValid() ? fallback.format('YYYY-MM-DD') : value;
          }
          return '';
        };

        const overviewChanges = [
          { fieldName: 'name', newValue: values.name, oldValue: originalOverviewValues.name },
          { fieldName: 'registration_no', newValue: values.registration_no, oldValue: originalOverviewValues.registration_no },
          { fieldName: 'entity', newValue: values.entity, oldValue: originalOverviewValues.entity },
          { fieldName: 'tin_no', newValue: values.tin_no, oldValue: originalOverviewValues.tin_no },
          {
            fieldName: 'incorporation_date',
            newValue: normalizeDate(values.incorporation_date),
            oldValue: normalizeDate(originalOverviewValues.incorporation_date),
          },
          { fieldName: 'year_end', newValue: values.year_end, oldValue: originalOverviewValues.year_end },
          { fieldName: 'contact_0_contact_number', newValue: values.contact_number, oldValue: originalOverviewValues.contact_number },
          { fieldName: 'contact_0_email', newValue: values.email, oldValue: originalOverviewValues.email },
          { fieldName: 'former_name_text', newValue: values.former_name, oldValue: originalOverviewValues.former_name },
        ];

        const changeRequests = overviewChanges
          .map((change) => ({
            ...change,
            newValue: normalizeValue(change.newValue),
            oldValue: normalizeValue(change.oldValue),
          }))
          .filter((change) => change.newValue !== change.oldValue);

        const businessId = parseInt(id, 10);

        const fileFields = ['name', 'sst_no', 'tin_no', 'trademark_logo', 'industry_specific_license', 'company_chop'] as const;
        const hasNewFiles = fileFields.some((field) => {
          const files = supportingFiles[field];
          return files && files.length > 0 && files[0].originFileObj;
        });

        if (changeRequests.length === 0 && !hasNewFiles) {
          message.info('No changes to submit');
          setIsEditing(false);
          setSearchParams({ tab: activeTab });
          return;
        }

        if (hasNewFiles) {
          const formData = new FormData();
          formData.append('business_id', String(businessId));
          changeRequests.forEach((change) => {
            if (change.fieldName === 'contact_0_contact_number') {
              formData.append('contact_number', change.newValue);
              return;
            }
            if (change.fieldName === 'contact_0_email') {
              formData.append('email', change.newValue);
              return;
            }
            formData.append(change.fieldName, change.newValue);
          });
          fileFields.forEach((field) => {
            const files = supportingFiles[field];
            if (files && files.length > 0 && files[0].originFileObj) {
              formData.append(`supporting_${field}`, files[0].originFileObj);
            }
          });
          await businessService.createOverviewRequest(formData);
        } else {
          const payload: any = { business_id: businessId };
          changeRequests.forEach((change) => {
            if (change.fieldName === 'contact_0_contact_number') {
              payload.contact_number = change.newValue;
              return;
            }
            if (change.fieldName === 'contact_0_email') {
              payload.email = change.newValue;
              return;
            }
            payload[change.fieldName] = change.newValue;
          });
          await businessService.createOverviewRequest(payload);
        }

        await loadBusiness(parseInt(id, 10));
        setIsEditing(false);
        setSubmitNotice({
          type: 'success',
          message: `${changeRequests.length + (hasNewFiles ? 1 : 0)} change request(s) submitted for review.`,
        });
        setSearchParams({ tab: activeTab });
        return;
      }
      if (isEdit && id && isSeniorOrJunior && activeTab === 'engagement' && !isCustomerPortalPage) {
        const normalizeValue = (value: any) => {
          if (value === undefined || value === null) return '';
          return String(value);
        };
        const normalizeDate = (value: any) => {
          if (!value) return '';
          if (dayjs.isDayjs(value)) return value.format('YYYY-MM-DD');
          if (typeof value === 'string') return value;
          return '';
        };
        const engagementValue = Array.isArray(values.engagement)
          ? values.engagement.join(',')
          : values.engagement;

        const engagementChanges = [
          { fieldName: 'engagement', newValue: engagementValue, oldValue: originalEngagementValues.engagement },
          { fieldName: 'advisor_staff_id', newValue: values.advisor_staff_id, oldValue: originalEngagementValues.advisor_staff_id },
          { fieldName: 'advisor_appoint_date', newValue: normalizeDate(values.advisor_appoint_date), oldValue: normalizeDate(originalEngagementValues.advisor_appoint_date) },
          { fieldName: 'advisor_resign_date', newValue: normalizeDate(values.advisor_resign_date), oldValue: normalizeDate(originalEngagementValues.advisor_resign_date) },
          { fieldName: 'account_staff_id', newValue: values.account_staff_id, oldValue: originalEngagementValues.account_staff_id },
          { fieldName: 'account_appoint_date', newValue: normalizeDate(values.account_appoint_date), oldValue: normalizeDate(originalEngagementValues.account_appoint_date) },
          { fieldName: 'account_resign_date', newValue: normalizeDate(values.account_resign_date), oldValue: normalizeDate(originalEngagementValues.account_resign_date) },
          { fieldName: 'tax_staff_id', newValue: values.tax_staff_id, oldValue: originalEngagementValues.tax_staff_id },
          { fieldName: 'tax_appoint_date', newValue: normalizeDate(values.tax_appoint_date), oldValue: normalizeDate(originalEngagementValues.tax_appoint_date) },
          { fieldName: 'tax_resign_date', newValue: normalizeDate(values.tax_resign_date), oldValue: normalizeDate(originalEngagementValues.tax_resign_date) },
          { fieldName: 'audit_staff_id', newValue: values.audit_staff_id, oldValue: originalEngagementValues.audit_staff_id },
          { fieldName: 'audit_appoint_date', newValue: normalizeDate(values.audit_appoint_date), oldValue: normalizeDate(originalEngagementValues.audit_appoint_date) },
          { fieldName: 'audit_resign_date', newValue: normalizeDate(values.audit_resign_date), oldValue: normalizeDate(originalEngagementValues.audit_resign_date) },
          { fieldName: 'secretary_staff_id', newValue: values.secretary_staff_id, oldValue: originalEngagementValues.secretary_staff_id },
          { fieldName: 'secretary_appoint_date', newValue: normalizeDate(values.secretary_appoint_date), oldValue: normalizeDate(originalEngagementValues.secretary_appoint_date) },
          { fieldName: 'secretary_resign_date', newValue: normalizeDate(values.secretary_resign_date), oldValue: normalizeDate(originalEngagementValues.secretary_resign_date) },
        ];

        const changeRequests = engagementChanges
          .map((change) => ({
            ...change,
            newValue: normalizeValue(change.newValue),
            oldValue: normalizeValue(change.oldValue),
          }))
          .filter((change) => change.newValue !== change.oldValue);

        const businessId = parseInt(id, 10);
        const fileFields = ['account_attachment', 'tax_attachment', 'audit_attachment', 'secretary_attachment'] as const;
        const hasNewFiles = fileFields.some((field) => {
          const files = engagementFiles[field];
          return files && files.length > 0 && files[0].originFileObj;
        });

        if (changeRequests.length === 0 && !hasNewFiles) {
          message.info('No changes to submit');
          setIsEditing(false);
          setSearchParams({ tab: activeTab });
          return;
        }

        if (hasNewFiles) {
          const formData = new FormData();
          formData.append('business_id', String(businessId));
          changeRequests.forEach((change) => {
            formData.append(change.fieldName, change.newValue);
          });
          fileFields.forEach((field) => {
            const files = engagementFiles[field];
            if (files && files.length > 0 && files[0].originFileObj) {
              formData.append(field, files[0].originFileObj);
            }
          });
          await businessService.createEngagementRequest(formData);
        } else {
          const payload: any = { business_id: businessId };
          changeRequests.forEach((change) => {
            payload[change.fieldName] = change.newValue;
          });
          await businessService.createEngagementRequest(payload);
        }

        await loadBusiness(parseInt(id, 10));
        setIsEditing(false);
        setSubmitNotice({
          type: 'success',
          message: `${changeRequests.length + (hasNewFiles ? 1 : 0)} change request(s) submitted for review.`,
        });
        setSearchParams({ tab: activeTab });
        return;
      }
      if (isEdit && id && isSeniorOrJunior && activeTab === 'address' && !isCustomerPortalPage) {
        const businessId = parseInt(id, 10);
        const changeRequestPromises: Promise<any>[] = [];
        const baseOriginalAddresses = originalAddresses || [];

        // Build a mapping from actualIndex (addresses array) to originalIndex
        const addressActualIndexToOriginalIndexMap = new Map<number, number>();
        addresses.forEach((address, actualIndex) => {
          const addressId = address.id || (address as any).id;
          const deletedAddressIds = new Set(deletedAddresses.map(d => d.data?.id).filter(idVal => idVal !== undefined && idVal !== null));
          if (addressId && deletedAddressIds.has(addressId)) {
            return;
          }
          let originalIndex = actualIndex;
          deletedAddresses.forEach(deleted => {
            if (deleted.index <= actualIndex) {
              originalIndex++;
            }
          });
          addressActualIndexToOriginalIndexMap.set(actualIndex, originalIndex);
        });

        for (const [key, newValue] of Object.entries(addressPendingChanges)) {
          const parts = key.split('_');
          if (parts.length < 3) continue;
          const actualIndex = parseInt(parts[1], 10);
          const field = parts.slice(2).join('_') as 'type' | 'address1' | 'address2';
          const originalIndex = addressActualIndexToOriginalIndexMap.get(actualIndex);
          if (originalIndex === undefined) continue;
          const oldAddress = baseOriginalAddresses[originalIndex];
          if (!oldAddress || !oldAddress.id) {
            continue;
          }
          const oldValue = oldAddress[field]?.toString() || '';
          const fieldName = `address_${originalIndex}_${field}`;
          if (oldValue !== newValue) {
            changeRequestPromises.push(
              businessService.createChangeRequest({
                business_id: businessId,
                field_name: fieldName,
                old_value: oldValue,
                new_value: newValue,
                status: 'pending',
              })
            );
          }
        }

        const originalAddressKeys = new Set<string>();
        baseOriginalAddresses.forEach(orig => {
          const key = `${orig.id || ''}_${orig.type || ''}`;
          if (key.trim()) {
            originalAddressKeys.add(key);
          }
        });
        const deletedAddressIds = new Set(deletedAddresses.map(d => d.data?.id).filter(idVal => idVal !== undefined && idVal !== null));

        addresses.forEach((address, currentIndex) => {
          const addressId = address.id || (address as any).id;
          if (addressId && deletedAddressIds.has(addressId)) {
            return;
          }
          let originalIndex = currentIndex;
          deletedAddresses.forEach(deleted => {
            if (deleted.index <= currentIndex) {
              originalIndex++;
            }
          });
          const positionExistsInOriginal = baseOriginalAddresses[originalIndex] !== undefined;
          const addressKey = addressId ? `${addressId}_${address.type || ''}` : '';
          const existsInOriginalByKey = addressKey && originalAddressKeys.has(addressKey);
          if (!addressId && !positionExistsInOriginal && !existsInOriginalByKey) {
            const pendingTypeKey = `address_${currentIndex}_type`;
            const pendingAddress1Key = `address_${currentIndex}_address1`;
            const pendingAddress2Key = `address_${currentIndex}_address2`;
            const mergedAddress = {
              type: (addressPendingChanges[pendingTypeKey] ?? address.type ?? 'main') as 'main' | 'branch',
              address1: addressPendingChanges[pendingAddress1Key] ?? address.address1 ?? '',
              address2: addressPendingChanges[pendingAddress2Key] ?? address.address2 ?? '',
            };
            if (mergedAddress.address1.trim()) {
              changeRequestPromises.push(
                businessService.createChangeRequest({
                  business_id: businessId,
                  field_name: 'address_add',
                  old_value: '',
                  new_value: JSON.stringify(mergedAddress),
                  status: 'pending',
                })
              );
            }
          }
        });

        deletedAddresses.forEach((item: { index: number; data: any }) => {
          if (item.data?.id) {
            const fieldName = `address_${item.index}_delete`;
            changeRequestPromises.push(
              businessService.createChangeRequest({
                business_id: businessId,
                field_name: fieldName,
                old_value: JSON.stringify(item.data),
                new_value: '',
                status: 'pending',
              })
            );
          }
        });

        if (changeRequestPromises.length > 0) {
          await Promise.all(changeRequestPromises);
          setAddressPendingChanges({});
          setDeletedAddresses([]);
          await loadBusiness(businessId);
          setIsEditing(false);
          setSubmitNotice({
            type: 'success',
            message: `${changeRequestPromises.length} change request(s) submitted for review.`,
          });
          setSearchParams({ tab: activeTab });
        } else {
          message.info('No changes to submit');
          setIsEditing(false);
          setSearchParams({ tab: activeTab });
        }
        return;
      }
      if (isEdit && id && isSeniorOrJunior && activeTab === 'nature' && !isCustomerPortalPage) {
        const businessId = parseInt(id, 10);
        const changeRequestPromises: Promise<any>[] = [];

        const normalizeValue = (value: any) => {
          if (value === undefined || value === null) return '';
          return String(value);
        };

        const natureFields = [
          {
            fieldName: 'nature_0_business_description',
            newValue: values.business_description,
            oldValue: originalNatureValues.business_description,
          },
          {
            fieldName: 'nature_0_trademark_logo',
            newValue: values.trademark_logo,
            oldValue: originalNatureValues.trademark_logo,
          },
          {
            fieldName: 'nature_0_industry_specific_license',
            newValue: values.industry_specific_license,
            oldValue: originalNatureValues.industry_specific_license,
          },
        ];
        const natureChanges = natureFields
          .map((change) => ({
            ...change,
            newValue: normalizeValue(change.newValue),
            oldValue: normalizeValue(change.oldValue),
          }))
          .filter((change) => change.newValue !== change.oldValue);

        const hasNatureFiles = ['trademark_logo', 'industry_specific_license'].some((field) => {
          const files = supportingFiles[field as keyof typeof supportingFiles];
          return files && files.length > 0 && files[0].originFileObj;
        });

        if (natureChanges.length > 0 || hasNatureFiles) {
          if (hasNatureFiles) {
            const formData = new FormData();
            formData.append('business_id', String(businessId));
            natureChanges.forEach((change) => {
              if (change.fieldName === 'nature_0_business_description') {
                formData.append('business_description', change.newValue);
                return;
              }
              if (change.fieldName === 'nature_0_trademark_logo') {
                formData.append('trademark_logo', change.newValue);
                return;
              }
              if (change.fieldName === 'nature_0_industry_specific_license') {
                formData.append('industry_specific_license', change.newValue);
                return;
              }
              formData.append(change.fieldName, change.newValue);
            });
            const trademarkFiles = supportingFiles.trademark_logo;
            if (trademarkFiles && trademarkFiles.length > 0 && trademarkFiles[0].originFileObj) {
              formData.append('trademark_logo_attachment', trademarkFiles[0].originFileObj);
            }
            const licenseFiles = supportingFiles.industry_specific_license;
            if (licenseFiles && licenseFiles.length > 0 && licenseFiles[0].originFileObj) {
              formData.append('industry_specific_license_attachment', licenseFiles[0].originFileObj);
            }
            await businessService.createNatureRequest(formData);
          } else {
            const payload: any = { business_id: businessId };
            natureChanges.forEach((change) => {
              if (change.fieldName === 'nature_0_business_description') {
                payload.business_description = change.newValue;
                return;
              }
              if (change.fieldName === 'nature_0_trademark_logo') {
                payload.trademark_logo = change.newValue;
                return;
              }
              if (change.fieldName === 'nature_0_industry_specific_license') {
                payload.industry_specific_license = change.newValue;
                return;
              }
              payload[change.fieldName] = change.newValue;
            });
            await businessService.createNatureRequest(payload);
          }
        }

        const actualIndexToOriginalIndexMap = new Map<number, number>();
        msicCodes.forEach((msic, actualIndex) => {
          let originalIndex = actualIndex;
          deletedMsicCodes.forEach((deleted) => {
            if (deleted.index <= actualIndex) {
              originalIndex++;
            }
          });
          actualIndexToOriginalIndexMap.set(actualIndex, originalIndex);
        });

        for (const [key, newValue] of Object.entries(msicCodePendingChanges)) {
          const parts = key.split('_');
          if (parts.length < 3) continue;
          const actualIndex = parseInt(parts[1], 10);
          const field = parts[2] as 'code' | 'description';
          const originalIndex = actualIndexToOriginalIndexMap.get(actualIndex);
          if (originalIndex === undefined) continue;
          const oldMsic = originalMsicCodes[originalIndex];
          if (!oldMsic) {
            continue;
          }
          const oldValue = oldMsic[field]?.toString() || '';
          const fieldName = `msic_${originalIndex}_${field}`;
          if (oldValue !== newValue) {
            changeRequestPromises.push(
              businessService.createChangeRequest({
                business_id: businessId,
                field_name: fieldName,
                old_value: oldValue,
                new_value: newValue,
                status: 'pending',
              })
            );
          }
        }

        const originalMsicKeys = new Set<string>();
        originalMsicCodes.forEach((orig) => {
          const key = `${orig.code || ''}_${orig.description || ''}`;
          if (key.trim()) {
            originalMsicKeys.add(key);
          }
        });

        msicCodes.forEach((msic, currentIndex) => {
          const msicKey = `${msic.code || ''}_${msic.description || ''}`;
          if (!msicKey.trim()) return;
          let originalIndex = currentIndex;
          deletedMsicCodes.forEach((deleted) => {
            if (deleted.index <= currentIndex) {
              originalIndex++;
            }
          });
          const positionExistsInOriginal = originalMsicCodes[originalIndex] !== undefined;
          const existsInOriginalByKey = originalMsicKeys.has(msicKey);
          if (!positionExistsInOriginal && !existsInOriginalByKey) {
            const pendingCodeKey = `msic_${currentIndex}_code`;
            const pendingDescriptionKey = `msic_${currentIndex}_description`;
            const codeValue = (msic.code && msic.code.trim()) || msicCodePendingChanges[pendingCodeKey] || '';
            const descriptionValue = (msic.description && msic.description.trim()) || msicCodePendingChanges[pendingDescriptionKey] || '';
            if (codeValue.trim()) {
              changeRequestPromises.push(
                businessService.createChangeRequest({
                  business_id: businessId,
                  field_name: 'msic_add',
                  old_value: '',
                  new_value: JSON.stringify({
                    code: codeValue,
                    description: descriptionValue,
                  }),
                  status: 'pending',
                })
              );
            }
          }
        });

        deletedMsicCodes.forEach((item: { index: number; data: any }) => {
          if (item.data) {
            const fieldName = `msic_${item.index}_delete`;
            changeRequestPromises.push(
              businessService.createChangeRequest({
                business_id: businessId,
                field_name: fieldName,
                old_value: JSON.stringify(item.data),
                new_value: '',
                status: 'pending',
              })
            );
          }
        });

        if (changeRequestPromises.length > 0 || natureChanges.length > 0 || hasNatureFiles) {
          await Promise.all(changeRequestPromises);
          setMsicCodePendingChanges({});
          setDeletedMsicCodes([]);
          await loadBusiness(businessId);
          setIsEditing(false);
          setSubmitNotice({
            type: 'success',
            message: `${changeRequestPromises.length + (natureChanges.length > 0 || hasNatureFiles ? 1 : 0)} change request(s) submitted for review.`,
          });
          setSearchParams({ tab: activeTab });
        } else {
          message.info('No changes to submit');
          setIsEditing(false);
          setSearchParams({ tab: activeTab });
        }
        return;
      }
      if (isEdit && id && isSeniorOrJunior && activeTab === 'financial' && !isCustomerPortalPage) {
        const businessId = parseInt(id, 10);
        const changeRequestPromises: Promise<any>[] = [];

        const actualIndexToOriginalIndexMap = new Map<number, number>();
        financials.forEach((financial, actualIndex) => {
          let originalIndex = actualIndex;
          deletedFinancials.forEach((deleted) => {
            if (deleted.index <= actualIndex) {
              originalIndex++;
            }
          });
          actualIndexToOriginalIndexMap.set(actualIndex, originalIndex);
        });

        for (const [key, newValue] of Object.entries(financialPendingChanges)) {
          const parts = key.split('_');
          if (parts.length < 3) continue;
          const actualIndex = parseInt(parts[1], 10);
          const field = parts.slice(2).join('_') as 'type' | 'bank' | 'branch_name' | 'bank_account';
          const originalIndex = actualIndexToOriginalIndexMap.get(actualIndex);
          if (originalIndex === undefined) continue;
          const oldFinancial = originalFinancials[originalIndex];
          if (!oldFinancial || !oldFinancial.id) {
            continue;
          }
          const oldValue = oldFinancial[field]?.toString() || '';
          const fieldName = `financial_${originalIndex}_${field}`;
          if (oldValue !== newValue) {
            changeRequestPromises.push(
              businessService.createChangeRequest({
                business_id: businessId,
                field_name: fieldName,
                old_value: oldValue,
                new_value: newValue,
                status: 'pending',
              })
            );
          }
        }

        const originalFinancialKeys = new Set<string>();
        originalFinancials.forEach((orig) => {
          const key = `${orig.id || ''}_${orig.type || ''}_${orig.bank || ''}_${orig.bank_account || ''}`;
          if (key.trim()) {
            originalFinancialKeys.add(key);
          }
        });

        financials.forEach((fin, currentIndex) => {
          const finId = (fin as any).id;
          let originalIndex = currentIndex;
          deletedFinancials.forEach((deleted) => {
            if (deleted.index <= currentIndex) {
              originalIndex++;
            }
          });
          const positionExistsInOriginal = originalFinancials[originalIndex] !== undefined;
          const finKey = finId ? `${finId}_${fin.type || ''}_${fin.bank || ''}_${fin.bank_account || ''}` : '';
          const existsInOriginalByKey = finKey && originalFinancialKeys.has(finKey);
          if (!finId && !positionExistsInOriginal && !existsInOriginalByKey) {
            const pendingTypeKey = `financial_${currentIndex}_type`;
            const pendingBankKey = `financial_${currentIndex}_bank`;
            const pendingBranchKey = `financial_${currentIndex}_branch_name`;
            const pendingAccountKey = `financial_${currentIndex}_bank_account`;
            const mergedFinancial = {
              type: financialPendingChanges[pendingTypeKey] ?? fin.type ?? '',
              bank: financialPendingChanges[pendingBankKey] ?? fin.bank ?? '',
              branch_name: financialPendingChanges[pendingBranchKey] ?? fin.branch_name ?? '',
              bank_account: financialPendingChanges[pendingAccountKey] ?? fin.bank_account ?? '',
            };
            if (
              mergedFinancial.type.trim()
              || mergedFinancial.bank.trim()
              || mergedFinancial.branch_name.trim()
              || mergedFinancial.bank_account.trim()
            ) {
              changeRequestPromises.push(
                businessService.createChangeRequest({
                  business_id: businessId,
                  field_name: 'financial_add',
                  old_value: '',
                  new_value: JSON.stringify(mergedFinancial),
                  status: 'pending',
                })
              );
            }
          }
        });

        deletedFinancials.forEach((item: { index: number; data: any }) => {
          if (item.data?.id) {
            const fieldName = `financial_${item.index}_delete`;
            changeRequestPromises.push(
              businessService.createChangeRequest({
                business_id: businessId,
                field_name: fieldName,
                old_value: JSON.stringify(item.data),
                new_value: '',
                status: 'pending',
              })
            );
          }
        });

        if (changeRequestPromises.length > 0) {
          await Promise.all(changeRequestPromises);
          setFinancialPendingChanges({});
          setDeletedFinancials([]);
          await loadBusiness(businessId);
          setIsEditing(false);
          setSubmitNotice({
            type: 'success',
            message: `${changeRequestPromises.length} change request(s) submitted for review.`,
          });
          setSearchParams({ tab: activeTab });
        } else {
          message.info('No changes to submit');
          setIsEditing(false);
          setSearchParams({ tab: activeTab });
        }
        return;
      }
      if (isEdit && id && isSeniorOrJunior && activeTab === 'directors' && !isCustomerPortalPage) {
        const businessId = parseInt(id, 10);
        const changeRequestPromises: Promise<any>[] = [];

        const actualIndexToOriginalIndexMap = new Map<number, number>();
        directors.forEach((director, actualIndex) => {
          let originalIndex = actualIndex;
          deletedDirectors.forEach((deleted) => {
            if (deleted.index <= actualIndex) {
              originalIndex++;
            }
          });
          actualIndexToOriginalIndexMap.set(actualIndex, originalIndex);
        });

        for (const [key, newValue] of Object.entries(directorPendingChanges)) {
          const parts = key.split('_');
          if (parts.length < 3) continue;
          const actualIndex = parseInt(parts[1], 10);
          const field = parts.slice(2).join('_') as keyof CreateBusinessDirectorData;
          const originalIndex = actualIndexToOriginalIndexMap.get(actualIndex);
          if (originalIndex === undefined) continue;
          const oldDirector = originalDirectors[originalIndex];
          if (!oldDirector || !oldDirector.id) {
            continue;
          }
          let oldValue = (oldDirector as any)[field];
          let newValueNormalized: any = newValue;
          if (field === 'customer_id') {
            oldValue = oldValue ? String(oldValue) : '';
            newValueNormalized = newValue ? String(newValue) : '';
          }
          const oldValueString = (oldValue ?? '').toString();
          const fieldName = `director_${originalIndex}_${field}`;
          if (oldValueString !== (newValueNormalized ?? '').toString()) {
            changeRequestPromises.push(
              businessService.createChangeRequest({
                business_id: businessId,
                field_name: fieldName,
                old_value: oldValueString,
                new_value: (newValueNormalized ?? '').toString(),
                status: 'pending',
              })
            );
          }
        }

        const originalDirectorKeys = new Set<string>();
        originalDirectors.forEach((orig) => {
          const key = `${orig.id || ''}_${orig.name || ''}_${orig.identification_no || ''}`;
          if (key.trim()) {
            originalDirectorKeys.add(key);
          }
        });

        directors.forEach((dir, currentIndex) => {
          const dirId = (dir as any).id;
          let originalIndex = currentIndex;
          deletedDirectors.forEach((deleted) => {
            if (deleted.index <= currentIndex) {
              originalIndex++;
            }
          });
          const positionExistsInOriginal = originalDirectors[originalIndex] !== undefined;
          const dirKey = dirId ? `${dirId}_${dir.name || ''}_${dir.identification_no || ''}` : '';
          const existsInOriginalByKey = dirKey && originalDirectorKeys.has(dirKey);
          if (!dirId && !positionExistsInOriginal && !existsInOriginalByKey) {
            const pendingCustomerKey = `director_${currentIndex}_customer_id`;
            const pendingNameKey = `director_${currentIndex}_name`;
            const pendingIdKey = `director_${currentIndex}_identification_no`;
            const pendingPosKey = `director_${currentIndex}_position`;
            const pendingAppKey = `director_${currentIndex}_appoint_date`;
            const pendingResKey = `director_${currentIndex}_resign_date`;
            const mergedDirector = {
              customer_id: directorPendingChanges[pendingCustomerKey]
                ? parseInt(directorPendingChanges[pendingCustomerKey], 10)
                : (dir.customer_id || undefined),
              name: directorPendingChanges[pendingNameKey] ?? dir.name ?? '',
              identification_no: directorPendingChanges[pendingIdKey] ?? dir.identification_no ?? '',
              position: directorPendingChanges[pendingPosKey] ?? dir.position ?? '',
              appoint_date: directorPendingChanges[pendingAppKey] ?? dir.appoint_date ?? '',
              resign_date: directorPendingChanges[pendingResKey] ?? dir.resign_date ?? '',
            };
            const hasVal =
              mergedDirector.name.trim()
              || mergedDirector.identification_no.trim()
              || mergedDirector.position.trim()
              || mergedDirector.appoint_date.trim()
              || mergedDirector.resign_date.trim();
            if (hasVal) {
              changeRequestPromises.push(
                businessService.createChangeRequest({
                  business_id: businessId,
                  field_name: 'director_add',
                  old_value: '',
                  new_value: JSON.stringify(mergedDirector),
                  status: 'pending',
                })
              );
            }
          }
        });

        deletedDirectors.forEach((item: { index: number; data: any }) => {
          if (item.data?.id) {
            const fieldName = `director_${item.index}_delete`;
            changeRequestPromises.push(
              businessService.createChangeRequest({
                business_id: businessId,
                field_name: fieldName,
                old_value: JSON.stringify(item.data),
                new_value: '',
                status: 'pending',
              })
            );
          }
        });

        if (changeRequestPromises.length > 0) {
          await Promise.all(changeRequestPromises);
          setDirectorPendingChanges({});
          setDeletedDirectors([]);
          await loadBusiness(businessId);
          setIsEditing(false);
          setSubmitNotice({
            type: 'success',
            message: `${changeRequestPromises.length} change request(s) submitted for review.`,
          });
          setSearchParams({ tab: activeTab });
        } else {
          message.info('No changes to submit');
          setIsEditing(false);
          setSearchParams({ tab: activeTab });
        }
        return;
      }
      if (isEdit && id && isSeniorOrJunior && activeTab === 'employers' && !isCustomerPortalPage) {
        const businessId = parseInt(id, 10);
        const normalizeValue = (value: any) => {
          if (value === undefined || value === null) return '';
          return String(value);
        };
        const employerFields: Array<keyof CreateBusinessEmployerData> = [
          'employer_no',
          'employer_no_password',
          'employer_account_no',
          'sst_id',
          'sst_password',
          'sst_account_no',
          'kwsp_id',
          'kwsp_password',
          'kwsp_account_no',
          'perkeso_id',
          'perkeso_password',
          'perkeso_account_no',
          'hrdc_id',
          'hrdc_password',
          'hrdc_account_no',
          'pcb',
        ];
        const payload: Record<string, any> = { business_id: businessId };
        let hasChanges = false;
        employerFields.forEach((field) => {
          const newValue = normalizeValue((employer as any)[field]);
          const oldValue = normalizeValue((originalEmployerValues as any)[field]);
          if (newValue !== oldValue) {
            payload[field] = newValue;
            hasChanges = true;
          }
        });
        if (!hasChanges) {
          message.info('No changes to submit');
          setIsEditing(false);
          setSearchParams({ tab: activeTab });
          return;
        }
        await businessService.createEmployersRequest(payload);
        await loadBusiness(businessId);
        setIsEditing(false);
        setEmployerPendingChanges({});
        setSubmitNotice({
          type: 'success',
          message: 'Employer request submitted for review.',
        });
        setSearchParams({ tab: activeTab });
        return;
      }
      if (isEdit && id && isSeniorOrJunior && activeTab === 'members' && !isCustomerPortalPage) {
        const businessId = parseInt(id, 10);
        const changeRequestPromises: Promise<any>[] = [];

        const actualIndexToOriginalIndexMap = new Map<number, number>();
        members.forEach((member, actualIndex) => {
          let originalIndex = actualIndex;
          deletedMembers.forEach((deleted) => {
            if (deleted.index <= actualIndex) {
              originalIndex++;
            }
          });
          actualIndexToOriginalIndexMap.set(actualIndex, originalIndex);
        });

        for (const [key, newValue] of Object.entries(memberPendingChanges)) {
          const parts = key.split('_');
          if (parts.length < 3) continue;
          const actualIndex = parseInt(parts[1], 10);
          const field = parts.slice(2).join('_') as keyof CreateBusinessMemberData;
          const originalIndex = actualIndexToOriginalIndexMap.get(actualIndex);
          if (originalIndex === undefined) continue;
          const oldMember = originalMembers[originalIndex];
          if (!oldMember || !oldMember.id) {
            continue;
          }
          let oldValue = (oldMember as any)[field];
          let newValueNormalized: any = newValue;
          if (field === 'share') {
            oldValue = oldValue ? String(oldValue) : '';
            newValueNormalized = newValue ? String(newValue) : '';
          }
          if (field === 'reference_id' || field === 'customer_id') {
            oldValue = oldValue ? String(oldValue) : '';
            newValueNormalized = newValue ? String(newValue) : '';
          }
          const fieldName = `member_${originalIndex}_${field}`;
          if ((oldValue || '') !== (newValueNormalized || '')) {
            changeRequestPromises.push(
              businessService.createChangeRequest({
                business_id: businessId,
                field_name: fieldName,
                old_value: (oldValue ?? '').toString(),
                new_value: (newValueNormalized ?? '').toString(),
                status: 'pending',
              })
            );
          }
        }

        const originalMemberKeys = new Set<string>();
        originalMembers.forEach((orig) => {
          const key = `${orig.id || ''}_${orig.name || ''}_${orig.identification_no || ''}`;
          if (key.trim()) {
            originalMemberKeys.add(key);
          }
        });

        members.forEach((mem, currentIndex) => {
          const memId = (mem as any).id;
          let originalIndex = currentIndex;
          deletedMembers.forEach((deleted) => {
            if (deleted.index <= currentIndex) {
              originalIndex++;
            }
          });
          const positionExistsInOriginal = originalMembers[originalIndex] !== undefined;
          const memKey = memId ? `${memId}_${mem.name || ''}_${mem.identification_no || ''}` : '';
          const existsInOriginalByKey = memKey && originalMemberKeys.has(memKey);
          if (!memId && !positionExistsInOriginal && !existsInOriginalByKey) {
            const pendingTypeKey = `member_${currentIndex}_type`;
            const pendingRefKey = `member_${currentIndex}_reference_id`;
            const pendingNameKey = `member_${currentIndex}_name`;
            const pendingIdKey = `member_${currentIndex}_identification_no`;
            const pendingShareKey = `member_${currentIndex}_share`;
            const pendingAppKey = `member_${currentIndex}_appoint_date`;
            const pendingResKey = `member_${currentIndex}_resign_date`;
            const mergedMember = {
              type: (memberPendingChanges[pendingTypeKey] ?? mem.type ?? undefined) as any,
              reference_id: memberPendingChanges[pendingRefKey]
                ? parseInt(memberPendingChanges[pendingRefKey], 10)
                : (mem.reference_id || undefined),
              customer_id: mem.type === 'customer'
                ? (memberPendingChanges[pendingRefKey] ? parseInt(memberPendingChanges[pendingRefKey], 10) : (mem.reference_id || mem.customer_id))
                : undefined,
              name: memberPendingChanges[pendingNameKey] ?? mem.name ?? '',
              identification_no: memberPendingChanges[pendingIdKey] ?? mem.identification_no ?? '',
              share: memberPendingChanges[pendingShareKey]
                ? parseFloat(memberPendingChanges[pendingShareKey])
                : (mem.share ? parseFloat(String(mem.share)) : 0),
              appoint_date: memberPendingChanges[pendingAppKey] ?? mem.appoint_date ?? '',
              resign_date: memberPendingChanges[pendingResKey] ?? mem.resign_date ?? '',
            };
            const hasVal =
              mergedMember.name.trim()
              || mergedMember.identification_no.trim()
              || mergedMember.share !== 0
              || mergedMember.appoint_date.trim()
              || mergedMember.resign_date.trim();
            if (hasVal) {
              changeRequestPromises.push(
                businessService.createChangeRequest({
                  business_id: businessId,
                  field_name: 'member_add',
                  old_value: '',
                  new_value: JSON.stringify(mergedMember),
                  status: 'pending',
                })
              );
            }
          }
        });

        deletedMembers.forEach((item: { index: number; data: any }) => {
          if (item.data?.id) {
            const fieldName = `member_${item.index}_delete`;
            changeRequestPromises.push(
              businessService.createChangeRequest({
                business_id: businessId,
                field_name: fieldName,
                old_value: JSON.stringify(item.data),
                new_value: '',
                status: 'pending',
              })
            );
          }
        });

        if (changeRequestPromises.length > 0) {
          await Promise.all(changeRequestPromises);
          setMemberPendingChanges({});
          setDeletedMembers([]);
          await loadBusiness(businessId);
          setIsEditing(false);
          setSubmitNotice({
            type: 'success',
            message: `${changeRequestPromises.length} change request(s) submitted for review.`,
          });
          setSearchParams({ tab: activeTab });
        } else {
          message.info('No changes to submit');
          setIsEditing(false);
          setSearchParams({ tab: activeTab });
        }
        return;
      }
      // Get all add change requests (both pending and approved) to filter them out from arrays
      // Add requests (both pending and approved) will be processed via approveChangeRequest API,
      // so we should exclude them from the submit data to avoid duplicate insertion
      const addRequests = changeRequests.filter(cr => 
        (cr.status === 'pending' || cr.status === 'approved') && 
        (cr.field_name === 'address_add' || 
         cr.field_name === 'msic_add' || 
         cr.field_name === 'financial_add' || 
         cr.field_name === 'director_add' || 
         cr.field_name === 'member_add' ||
         cr.field_name?.match(/^address_\d+_add$/) ||
         cr.field_name?.match(/^msic_\d+_add$/) ||
         cr.field_name?.match(/^financial_\d+_add$/) ||
         cr.field_name?.match(/^director_\d+_add$/) ||
         cr.field_name?.match(/^member_\d+_add$/))
      );
      
      // Create sets to track items that should be excluded (from all add requests)
      const excludedAddressItems = new Set<string>();
      const excludedMsicItems = new Set<string>();
      const excludedFinancialItems = new Set<string>();
      const excludedDirectorItems = new Set<string>();
      const excludedMemberItems = new Set<string>();
      
      addRequests.forEach(cr => {
        try {
          const newValue = JSON.parse(cr.new_value || '{}');
          if (cr.field_name === 'address_add' || cr.field_name?.match(/^address_\d+_add$/)) {
            const key = `${newValue.type || ''}_${newValue.address1 || ''}_${newValue.address2 || ''}`;
            excludedAddressItems.add(key);
          } else if (cr.field_name === 'msic_add' || cr.field_name?.match(/^msic_\d+_add$/)) {
            const key = `${newValue.code || ''}_${newValue.description || ''}`;
            excludedMsicItems.add(key);
          } else if (cr.field_name === 'financial_add' || cr.field_name?.match(/^financial_\d+_add$/)) {
            const key = `${newValue.type || ''}_${newValue.bank || ''}_${newValue.bank_account || ''}`;
            excludedFinancialItems.add(key);
          } else if (cr.field_name === 'director_add' || cr.field_name?.match(/^director_\d+_add$/)) {
            const key = `${newValue.name || ''}_${newValue.identification_no || ''}`;
            excludedDirectorItems.add(key);
          } else if (cr.field_name === 'member_add' || cr.field_name?.match(/^member_\d+_add$/)) {
            const key = `${newValue.name || ''}_${newValue.identification_no || ''}`;
            excludedMemberItems.add(key);
          }
        } catch (e) {
          // Ignore parsing errors
        }
      });
      
      // Filter arrays to exclude items from add requests
      const filteredAddresses = addresses.filter(a => {
        const key = `${a.type || ''}_${a.address1 || ''}_${a.address2 || ''}`;
        return !excludedAddressItems.has(key);
      });
      
      const filteredMsicCodes = msicCodes.filter(m => {
        const key = `${m.code || ''}_${m.description || ''}`;
        return !excludedMsicItems.has(key);
      });
      
      const filteredFinancials = financials.filter(f => {
        const key = `${f.type || ''}_${f.bank || ''}_${f.bank_account || ''}`;
        return !excludedFinancialItems.has(key);
      });
      
      const filteredDirectors = directors.filter(d => {
        const key = `${d.name || ''}_${d.identification_no || ''}`;
        return !excludedDirectorItems.has(key);
      }).map(d => ({
        ...d,
        customer_id: d.customer_id || undefined,
        appoint_date: d.appoint_date && d.appoint_date.trim() ? normalizeDateOnly(d.appoint_date) : null,
        resign_date: d.resign_date && d.resign_date.trim() ? normalizeDateOnly(d.resign_date) : null,
      }));
      
      
      const filteredMembers = members.filter(m => {
        const key = `${m.name || ''}_${m.identification_no || ''}`;
        return !excludedMemberItems.has(key);
      }).map(m => ({
        ...m,
        type: m.type || undefined,
        reference_id: m.reference_id || undefined,
        customer_id: m.type === 'customer'
          ? (m.reference_id || m.customer_id || undefined)
          : undefined,
        appoint_date: m.appoint_date && m.appoint_date.trim() ? normalizeDateOnly(m.appoint_date) : null,
        resign_date: m.resign_date && m.resign_date.trim() ? normalizeDateOnly(m.resign_date) : null,
        share: m.share ? parseFloat(String(m.share)) : null,
      }));
      
      // Convert engagement array to string if needed
      const engagementInput = values.engagement !== undefined
        ? values.engagement
        : originalEngagementValues.engagement;
      const engagementValue = Array.isArray(engagementInput) 
        ? engagementInput.join(',') 
        : (engagementInput ?? '');
      
      // Calculate business type based on engagement dates
      const calculatedType = calculateBusinessType();
      
      // Create contact from single contact fields and filtered addresses
      const contactData = values.contact_number || values.email || filteredAddresses.length > 0
        ? [{
            contact_number: values.contact_number || '',
            email: values.email || '',
            main_address1: filteredAddresses.find(a => a.type === 'main')?.address1 || '',
            main_address2: filteredAddresses.find(a => a.type === 'main')?.address2 || '',
            branch_address1: filteredAddresses.find(a => a.type === 'branch')?.address1 || '',
            branch_address2: filteredAddresses.find(a => a.type === 'branch')?.address2 || '',
            business_license: false,
            able_to_add_more_branches: false,
          }]
        : [];
      
      // Create nature from single nature fields and filtered MSIC codes.
      // Use original values when fields are not present in the submitted values (tab not visited).
      const resolvedBusinessDescription =
        values.business_description !== undefined
          ? values.business_description
          : originalNatureValues.business_description;
      const resolvedTrademarkLogo =
        values.trademark_logo !== undefined
          ? values.trademark_logo
          : originalNatureValues.trademark_logo;
      const resolvedIndustryLicense =
        values.industry_specific_license !== undefined
          ? values.industry_specific_license
          : originalNatureValues.industry_specific_license;
      const hasNatureValues =
        !!resolvedBusinessDescription
        || !!resolvedTrademarkLogo
        || !!resolvedIndustryLicense
        || filteredMsicCodes.length > 0;
      const natureData = hasNatureValues
        ? [{
            business_description: resolvedBusinessDescription || '',
            trademark_logo: resolvedTrademarkLogo || '',
            industry_specific_license: resolvedIndustryLicense || '',
            msic_code_1: filteredMsicCodes[0]?.code || '',
            msic_description_1: filteredMsicCodes[0]?.description || '',
            msic_code_2: filteredMsicCodes[1]?.code || '',
            msic_description_2: filteredMsicCodes[1]?.description || '',
            msic_code_3: filteredMsicCodes[2]?.code || '',
            msic_description_3: filteredMsicCodes[2]?.description || '',
            msic_code_4: filteredMsicCodes[3]?.code || '',
            msic_description_4: filteredMsicCodes[3]?.description || '',
            msic_code_5: filteredMsicCodes[4]?.code || '',
            msic_description_5: filteredMsicCodes[4]?.description || '',
          }]
        : [];
      
      // Format addresses for submission (remove id if it's a new address)
      // Filter out invalid addresses and ensure type is valid
      const formattedAddresses = filteredAddresses
        .filter(addr => {
          // Only include addresses with valid type ('main' or 'branch')
          const validType = addr.type === 'main' || addr.type === 'branch';
          // Include if it has at least address1 or address2 filled
          const hasContent = (addr.address1 && addr.address1.trim()) || (addr.address2 && addr.address2.trim());
          return validType && hasContent;
        })
        .map(addr => ({
          type: addr.type === 'main' || addr.type === 'branch' ? addr.type : 'main',
          address: (addr.address1 && addr.address1.trim()) || (addr.address && (addr.address as string).trim()) || null,
          attachment: (addr as any).attachment || undefined,
          // Don't include id for new addresses (backend will create new ones)
          ...(addr.id ? { id: addr.id } : {}),
        }));

      const normalizeEmployerField = (value?: string) =>
        value && value.trim() ? value : null;
      const normalizedEmployer = {
        employer_no: normalizeEmployerField(employer.employer_no),
        employer_no_password: normalizeEmployerField((employer as any).employer_no_password),
        employer_account_no: normalizeEmployerField((employer as any).employer_account_no),
        sst_id: normalizeEmployerField((employer as any).sst_id),
        sst_password: normalizeEmployerField((employer as any).sst_password),
        sst_account_no: normalizeEmployerField((employer as any).sst_account_no),
        kwsp_id: normalizeEmployerField(employer.kwsp_id),
        kwsp_password: normalizeEmployerField(employer.kwsp_password),
        kwsp_account_no: normalizeEmployerField((employer as any).kwsp_account_no),
        perkeso_id: normalizeEmployerField(employer.perkeso_id),
        perkeso_password: normalizeEmployerField(employer.perkeso_password),
        perkeso_account_no: normalizeEmployerField((employer as any).perkeso_account_no),
        hrdc_id: normalizeEmployerField(employer.hrdc_id),
        hrdc_password: normalizeEmployerField(employer.hrdc_password),
        hrdc_account_no: normalizeEmployerField((employer as any).hrdc_account_no),
        pcb: normalizeEmployerField(employer.pcb),
      };
      const hasEmployerValues = Object.values(normalizedEmployer).some((v) => v);

      const submitData: any = {
        ...values,
        type: calculatedType, // Set type based on engagement dates
        engagement: engagementValue,
        incorporation_date: values.incorporation_date ? (typeof values.incorporation_date === 'string' ? values.incorporation_date : dayjs(values.incorporation_date).format('YYYY-MM-DD')) : undefined,
        constitution: values.constitution ?? false,
        common_seal: values.common_seal ?? false,
        contacts: contactData.length > 0 ? contactData : undefined,
        // Always include addresses array for updates (even if empty), or if there are addresses for creates
        // For updates, backend expects addresses array to delete existing and create new ones
        addresses: isEdit ? formattedAddresses : (formattedAddresses.length > 0 ? formattedAddresses : undefined),
        natures: natureData.length > 0 ? natureData : undefined,
        clear_natures: isEdit
          && natureData.length === 0
          && ['business_description', 'trademark_logo', 'industry_specific_license'].some((field) =>
            Object.prototype.hasOwnProperty.call(values, field)
          )
          ? true
          : undefined,
        financials: filteredFinancials.length > 0 ? filteredFinancials : undefined,
        employers: hasEmployerValues
          ? [normalizedEmployer]
          : (isEdit && !isCustomerPortalPage ? [] : undefined),
        // For updates, send empty arrays to allow full deletion
        directors: (isEdit && !isCustomerPortalPage)
          ? filteredDirectors
          : (filteredDirectors.length > 0 ? filteredDirectors : undefined),
        members: (isEdit && !isCustomerPortalPage)
          ? filteredMembers
          : (filteredMembers.length > 0 ? filteredMembers : undefined),
        supporting: values.former_name
          ? {
              former_name_text: values.former_name,
            }
          : undefined,
      };

      // Ensure cleared engagement fields are sent as empty strings so backend can null them
      if (isEdit) {
        const engagementClearableFields = [
          'engagement',
          'advisor_staff_id',
          'advisor_appoint_date',
          'advisor_resign_date',
          'account_staff_id',
          'account_appoint_date',
          'account_resign_date',
          'tax_staff_id',
          'tax_appoint_date',
          'tax_resign_date',
          'audit_staff_id',
          'audit_appoint_date',
          'audit_resign_date',
          'secretary_staff_id',
          'secretary_appoint_date',
          'secretary_resign_date',
          'account_date',
          'tax_date',
          'audit_date',
          'secretary_date',
        ] as const;
        engagementClearableFields.forEach((field) => {
          const fieldPresent = Object.prototype.hasOwnProperty.call(values, field);
          if (!fieldPresent && submitData[field] === undefined) {
            submitData[field] = (originalEngagementValues as any)[field] ?? '';
            return;
          }
          if (fieldPresent && submitData[field] === undefined) {
            submitData[field] = '';
          }
        });
      }
      
      // For updates, ensure required fields are included
      if (isEdit && id && !isCustomerPortalPage) {
        const formValues = form.getFieldsValue();
        if (!submitData.customer_id && formValues.customer_id) {
          submitData.customer_id = formValues.customer_id;
        }
        if (!submitData.name && formValues.name) {
          submitData.name = formValues.name;
        }
        // Ensure type is included from form or state
        if (!submitData.type && (formValues.type || businessType)) {
          submitData.type = formValues.type || businessType;
        }
      }
      
      // Remove single contact fields from submitData (they're now in contacts array)
      delete submitData.contact_number;
      delete submitData.email;
      
      // Remove single nature fields from submitData (they're now in natures array)
      delete submitData.business_description;
      delete submitData.trademark_logo;
      delete submitData.industry_specific_license;
      delete submitData.former_name;

      const normalizeDateField = (value: any): string | undefined => {
        if (!value) return undefined;
        if (typeof value === 'string') return value;
        if (dayjs.isDayjs(value)) return value.format('YYYY-MM-DD');
        const asDate = new Date(value);
        if (!Number.isNaN(asDate.getTime())) return dayjs(asDate).format('YYYY-MM-DD');
        return undefined;
      };

      const engagementDateFields = [
        'advisor_appoint_date',
        'advisor_resign_date',
        'account_appoint_date',
        'account_resign_date',
        'tax_appoint_date',
        'tax_resign_date',
        'audit_appoint_date',
        'audit_resign_date',
        'secretary_appoint_date',
        'secretary_resign_date',
        'account_date',
        'tax_date',
        'audit_date',
        'secretary_date',
      ];

      engagementDateFields.forEach((field) => {
        if (submitData[field]) {
          submitData[field] = normalizeDateField(submitData[field]);
        }
      });

      if (isEdit && id) {
        if (isCustomerPortalPage) {
          // Customer portal: create change requests instead of direct update
          // Reference: CustomerPersonalDetail.tsx handleSave logic
          const changeRequestPromises: Promise<any>[] = [];
          const businessId = parseInt(id, 10);
          
          // Get original business data for comparison
          const originalBusiness = await customerApi.get(`/customer/businesses/${businessId}`).then(res => res.data?.data || res.data);
          const originalFirstContact = originalBusiness.contacts && originalBusiness.contacts.length > 0 ? originalBusiness.contacts[0] : null;
          
          // Only process fields that are in pendingChanges (user actually modified them)
          // This is the key difference from before - we only check modified fields
          for (const [fieldName, newValue] of Object.entries(pendingChanges)) {
            let oldValue: any;
            
            // Handle overview fields
            if (['name', 'registration_no', 'entity', 'sst_no', 'tin_no', 'year_end'].includes(fieldName)) {
              oldValue = originalBusiness[fieldName];
            }
            // Handle date fields
            else if (fieldName === 'incorporation_date') {
              oldValue = originalBusiness.incorporation_date;
            }
            // Handle contact fields
            else if (fieldName === 'contact_number' && originalFirstContact) {
              oldValue = originalFirstContact.contact_number;
            }
            else if (fieldName === 'email' && originalFirstContact) {
              oldValue = originalFirstContact.email;
            }
            else {
              // Unknown field, skip
              continue;
            }
            
            // Normalize values for comparison
            let normalizedOldValue: string;
            let normalizedNewValue: string;
            
            if (fieldName === 'incorporation_date') {
              // Handle date field
              normalizedOldValue = oldValue ? (typeof oldValue === 'string' ? dayjs(oldValue).format('YYYY-MM-DD') : dayjs(oldValue).format('YYYY-MM-DD')) : '';
              normalizedNewValue = newValue ? (typeof newValue === 'string' ? dayjs(newValue).format('YYYY-MM-DD') : dayjs(newValue).format('YYYY-MM-DD')) : '';
            } else {
              // Handle regular fields
              normalizedOldValue = (oldValue === null || oldValue === undefined || oldValue === '') ? '' : String(oldValue).trim();
              normalizedNewValue = (newValue === null || newValue === undefined || newValue === '') ? '' : String(newValue).trim();
            }
            
            // Skip if values are the same
            if (normalizedOldValue === normalizedNewValue) {
              continue;
            }
            
            // Skip if newValue is empty (unless it's a date)
            if (!normalizedNewValue && fieldName !== 'incorporation_date') {
              continue;
            }
            
            // Determine the field_name for change request
            let changeRequestFieldName = fieldName;
            if (fieldName === 'contact_number') {
              changeRequestFieldName = 'contact_0_contact_number';
            } else if (fieldName === 'email') {
              changeRequestFieldName = 'contact_0_email';
            }
            
            changeRequestPromises.push(
              customerApi.post('/customer/business-change-requests', {
                business_id: businessId,
                field_name: changeRequestFieldName,
                old_value: normalizedOldValue || '',
                new_value: normalizedNewValue || '',
                status: 'pending',
              })
            );
          }

          // Handle address pending changes (field-level changes for existing addresses)
          const originalAddresses: CreateBusinessAddressData[] = [];
          if (originalBusiness.contacts && originalBusiness.contacts.length > 0) {
            originalBusiness.contacts.forEach((contact: any) => {
              if (contact.main_address1 || contact.main_address2) {
                originalAddresses.push({
                  id: contact.id,
                  type: 'main',
                  address1: contact.main_address1 || '',
                  address2: contact.main_address2 || '',
                });
              }
              if (contact.branch_address1 || contact.branch_address2) {
                originalAddresses.push({
                  id: contact.id,
                  type: 'branch',
                  address1: contact.branch_address1 || '',
                  address2: contact.branch_address2 || '',
                });
              }
            });
          }

          // Build a mapping from actualIndex (in addresses array) to originalIndex
          // This is needed because handleAddressFieldChange uses actualIndex, but we need originalIndex to find the original value
          const addressActualIndexToOriginalIndexMap = new Map<number, number>();
          addresses.forEach((address, actualIndex) => {
            const addressId = address.id || (address as any).id;
            // Skip if address is marked for deletion
            const deletedAddressIds = new Set(deletedAddresses.map(d => d.data?.id).filter(id => id !== undefined && id !== null));
            if (addressId && deletedAddressIds.has(addressId)) {
              return;
            }
            // Calculate originalIndex: actualIndex + number of deleted items before this position
            let originalIndex = actualIndex;
            deletedAddresses.forEach(deleted => {
              if (deleted.index <= actualIndex) {
                originalIndex++;
              }
            });
            addressActualIndexToOriginalIndexMap.set(actualIndex, originalIndex);
          });

          for (const [key, newValue] of Object.entries(addressPendingChanges)) {
            const parts = key.split('_');
            if (parts.length < 3) continue;
            const actualIndex = parseInt(parts[1], 10); // This is the actual index in addresses array
            const field = parts.slice(2).join('_') as 'type' | 'address1' | 'address2';
            
            // Convert actualIndex to originalIndex using the mapping
            const originalIndex = addressActualIndexToOriginalIndexMap.get(actualIndex);
            if (originalIndex === undefined) {
              // Skip if we can't find the mapping (shouldn't happen, but be safe)
              continue;
            }
            
            // Use originalIndex to find the original address
            const oldAddress = originalAddresses[originalIndex];
            // If this address is new (no original entry with id), skip field-level change requests;
            // it will be represented by the address_add request.
            if (!oldAddress || !oldAddress.id) {
              continue;
            }
            const oldValue = oldAddress[field]?.toString() || '';
            const fieldName = `address_${originalIndex}_${field}`; // Use originalIndex for field name
            if (oldValue !== newValue) {
              changeRequestPromises.push(
                customerApi.post('/customer/business-change-requests', {
                  business_id: businessId,
                  field_name: fieldName,
                  old_value: oldValue,
                  new_value: newValue,
                  status: 'pending',
                })
              );
            }
          }

          // Handle new addresses (no id) -> add requests
          // Strategy: Collect all new addresses (not in original) and create a single address_add request
          // Similar to msic_add logic
          const originalAddressKeys = new Set<string>();
          originalAddresses.forEach(orig => {
            const key = `${orig.id || ''}_${orig.type || ''}`;
            if (key.trim()) {
              originalAddressKeys.add(key);
            }
          });
          
          // Create one address_add request for each new address row
          const deletedAddressIds = new Set(deletedAddresses.map(d => d.data?.id).filter(id => id !== undefined && id !== null));
          
          addresses.forEach((address, currentIndex) => {
            const addressId = address.id || (address as any).id;
            
            // Skip if address is marked for deletion
            if (addressId && deletedAddressIds.has(addressId)) {
              return;
            }
            
            // Calculate the original index: current index + number of deleted items before this position
            let originalIndex = currentIndex;
            deletedAddresses.forEach(deleted => {
              if (deleted.index <= currentIndex) {
                originalIndex++;
              }
            });
            
            // Check if this position exists in original array
            // If originalAddresses[originalIndex] exists, this is a modification of an existing item
            // (handled by field-level change requests), not a new item
            const positionExistsInOriginal = originalAddresses[originalIndex] !== undefined;
            
            // Also check if id+type matches any original address
            const addressKey = addressId ? `${addressId}_${address.type || ''}` : '';
            const existsInOriginalByKey = addressKey && originalAddressKeys.has(addressKey);
            
            // Only consider it "new" if:
            // 1. No addressId (truly new, not an existing address)
            // 2. Position doesn't exist in original (truly new position)
            // 3. AND id+type doesn't match any original address
            // Important: If addressId exists, it's definitely not a new address, even if position doesn't match
            if (!addressId && !positionExistsInOriginal && !existsInOriginalByKey) {
              // This is a truly new address (new position and new content)
              // Get values from addresses and addressPendingChanges
              const pendingTypeKey = `address_${currentIndex}_type`;
              const pendingAddress1Key = `address_${currentIndex}_address1`;
              const pendingAddress2Key = `address_${currentIndex}_address2`;
              const mergedAddress = {
                type: (addressPendingChanges[pendingTypeKey] ?? address.type ?? 'main') as 'main' | 'branch',
                address1: addressPendingChanges[pendingAddress1Key] ?? address.address1 ?? '',
                address2: addressPendingChanges[pendingAddress2Key] ?? address.address2 ?? '',
              };
              
              // Only create request if at least address1 has a value (non-empty)
              if (mergedAddress.address1.trim()) {
                // Create one address_add request for each new address (not an array)
                changeRequestPromises.push(
                  customerApi.post('/customer/business-change-requests', {
                    business_id: businessId,
                    field_name: 'address_add',
                    old_value: '',
                    new_value: JSON.stringify(mergedAddress), // Single object, not array
                    status: 'pending',
                  })
                );
              }
            }
          });

          // Handle deleted addresses
          deletedAddresses.forEach((item: { index: number; data: any }) => {
            if (item.data?.id) {
              const fieldName = `address_${item.index}_delete`;
              changeRequestPromises.push(
                customerApi.post('/customer/business-change-requests', {
                  business_id: businessId,
                  field_name: fieldName,
                  old_value: JSON.stringify(item.data),
                  new_value: null,
                  status: 'pending',
                })
              );
            }
          });

          // Handle MSIC code field changes
          const originalMsicCodes: Array<{ code: string; description: string }> = [];
          if (originalBusiness.natures && originalBusiness.natures.length > 0) {
            const nature = originalBusiness.natures[0];
            const codes = [
              nature.msic_code_1,
              nature.msic_code_2,
              nature.msic_code_3,
              nature.msic_code_4,
              nature.msic_code_5,
            ];
            codes.forEach((code) => {
              if (code) {
                originalMsicCodes.push({
                  code: code,
                  description: '', // Description will be stored separately
                });
              }
            });
          }
          
          console.log('BusinessForm: Original MSIC codes:', originalMsicCodes);
          console.log('BusinessForm: Current msicCodes:', msicCodes);
          console.log('BusinessForm: msicCodePendingChanges:', msicCodePendingChanges);
          console.log('BusinessForm: deletedMsicCodes:', deletedMsicCodes);

          // Create a set of original MSIC codes (by code+description) to identify new ones
          const originalMsicKeysForCheck = new Set<string>();
          originalMsicCodes.forEach(orig => {
            const key = `${orig.code || ''}_${orig.description || ''}`;
            if (key.trim()) {
              originalMsicKeysForCheck.add(key);
            }
          });
          
          // Create a set of new MSIC codes (by code+description) to skip field-level change requests for them
          const newMsicKeys = new Set<string>();
          msicCodes.forEach((msic) => {
            const msicKey = `${msic.code || ''}_${msic.description || ''}`;
            if (msicKey.trim() && !originalMsicKeysForCheck.has(msicKey)) {
              newMsicKeys.add(msicKey);
            }
          });

          // Build a mapping from actualIndex (in msicCodes array) to originalIndex (_crIndex)
          // This is needed because handleMsicCodeFieldChange uses actualIndex, but we need originalIndex to find the original value
          const actualIndexToOriginalIndexMap = new Map<number, number>();
          msicCodes.forEach((msic, actualIndex) => {
            // Calculate originalIndex: actualIndex + number of deleted items before this position
            let originalIndex = actualIndex;
            deletedMsicCodes.forEach(deleted => {
              if (deleted.index <= actualIndex) {
                originalIndex++;
              }
            });
            actualIndexToOriginalIndexMap.set(actualIndex, originalIndex);
          });
          
          console.log('BusinessForm: actualIndexToOriginalIndexMap:', Array.from(actualIndexToOriginalIndexMap.entries()));

          for (const [key, newValue] of Object.entries(msicCodePendingChanges)) {
            const parts = key.split('_');
            if (parts.length < 3) continue;
            const actualIndex = parseInt(parts[1], 10); // This is the actual index in msicCodes array
            const field = parts[2] as 'code' | 'description';
            
            console.log(`BusinessForm: Processing msicCodePendingChanges key: ${key}, actualIndex: ${actualIndex}, field: ${field}, newValue: ${newValue}`);
            
            // Convert actualIndex to originalIndex using the mapping
            const originalIndex = actualIndexToOriginalIndexMap.get(actualIndex);
            if (originalIndex === undefined) {
              console.log(`BusinessForm: Skipping - no mapping found for actualIndex ${actualIndex}`);
              continue;
            }
            
            console.log(`BusinessForm: Mapped actualIndex ${actualIndex} to originalIndex ${originalIndex}`);
            
            // Use originalIndex to find the original MSIC code
            // If originalIndex exists in originalMsicCodes, this is a modification, not a new item
            const oldMsic = originalMsicCodes[originalIndex];
            // If this MSIC code is new (no original entry at this originalIndex), skip field-level change requests;
            // it will be represented by the msic_add request.
            if (!oldMsic) {
              console.log(`BusinessForm: Skipping - no original MSIC at originalIndex ${originalIndex} (this is a new item)`);
              continue;
            }
            
            // If we have an oldMsic at originalIndex, this is definitely a modification, not a new item
            // So we should create a field-level change request regardless of whether the new value matches any original
            const oldValue = oldMsic[field]?.toString() || '';
            const fieldName = `msic_${originalIndex}_${field}`; // Use originalIndex for field name
            console.log(`BusinessForm: Comparing oldValue: "${oldValue}" vs newValue: "${newValue}"`);
            if (oldValue !== newValue) {
              console.log(`BusinessForm: Creating field-level change request: ${fieldName}, old: ${oldValue}, new: ${newValue}`);
              changeRequestPromises.push(
                customerApi.post('/customer/business-change-requests', {
                  business_id: businessId,
                  field_name: fieldName,
                  old_value: oldValue,
                  new_value: newValue,
                  status: 'pending',
                })
              );
            } else {
              console.log(`BusinessForm: No change detected for ${fieldName} (oldValue === newValue)`);
            }
          }

          // Handle new MSIC codes -> add requests
          // Strategy: Create one msic_add request for each new MSIC code row
          // Important: Only consider it "new" if:
          // 1. The position doesn't exist in original (originalMsicCodes[currentIndex] is undefined)
          // 2. AND the code+description doesn't match any original MSIC code
          // This prevents modified existing rows from being treated as new rows
          const originalMsicKeys = new Set<string>();
          originalMsicCodes.forEach(orig => {
            const key = `${orig.code || ''}_${orig.description || ''}`;
            if (key.trim()) { // Only add non-empty keys
              originalMsicKeys.add(key);
            }
          });
          
          msicCodes.forEach((msic, currentIndex) => {
            const msicKey = `${msic.code || ''}_${msic.description || ''}`;
            
            // Skip empty MSIC codes (no code and no description)
            if (!msicKey.trim()) {
              return;
            }
            
            // Calculate the original index: current index + number of deleted items before this position
            let originalIndex = currentIndex;
            deletedMsicCodes.forEach(deleted => {
              if (deleted.index <= currentIndex) {
                originalIndex++;
              }
            });
            
            // Check if this position exists in original array
            // If originalMsicCodes[originalIndex] exists, this is a modification of an existing item
            // (handled by field-level change requests), not a new item
            const positionExistsInOriginal = originalMsicCodes[originalIndex] !== undefined;
            
            // Also check if code+description matches any original MSIC code
            const existsInOriginalByKey = originalMsicKeys.has(msicKey);
            
            // Only consider it "new" if:
            // 1. Position doesn't exist in original (truly new position)
            // 2. AND code+description doesn't match any original MSIC code
            if (!positionExistsInOriginal && !existsInOriginalByKey) {
              // This is a truly new MSIC code (new position and new content)
              // Get values from msicCodes (handleMsicCodeFieldChange updates it)
              // Also check msicCodePendingChanges as fallback
              const pendingCodeKey = `msic_${currentIndex}_code`;
              const pendingDescriptionKey = `msic_${currentIndex}_description`;
              const codeValue = (msic.code && msic.code.trim()) || msicCodePendingChanges[pendingCodeKey] || '';
              const descriptionValue = (msic.description && msic.description.trim()) || msicCodePendingChanges[pendingDescriptionKey] || '';
              
              // Only create request if at least code has a value (non-empty)
              if (codeValue.trim()) {
                // Create one msic_add request for each new MSIC code (not an array)
                changeRequestPromises.push(
                  customerApi.post('/customer/business-change-requests', {
                    business_id: businessId,
                    field_name: 'msic_add',
                    old_value: '',
                    new_value: JSON.stringify({
                      code: codeValue,
                      description: descriptionValue,
                    }), // Single object, not array
                    status: 'pending',
                  })
                );
              }
            }
          });

          // Handle deleted MSIC codes
          deletedMsicCodes.forEach((item: { index: number; data: any }) => {
            // MSIC codes don't have independent ids, but we can still create delete requests
            // based on the index and original data
            if (item.data) {
              const fieldName = `msic_${item.index}_delete`;
              changeRequestPromises.push(
                customerApi.post('/customer/business-change-requests', {
                  business_id: businessId,
                  field_name: fieldName,
                  old_value: JSON.stringify(item.data),
                  new_value: null,
                  status: 'pending',
                })
              );
            }
          });

          // Handle Financial field changes
          const originalFinancials: CreateBusinessFinancialData[] = [];
          if (originalBusiness.financials && originalBusiness.financials.length > 0) {
            originalBusiness.financials.forEach((fin: any) => {
              originalFinancials.push({
                id: fin.id,
                type: fin.type || '',
                bank: fin.bank || '',
                branch_name: fin.branch_name || '',
                bank_account: fin.bank_account || '',
              });
            });
          }

          // Create a set of original financials (by id+type+bank+account) to identify new ones
          const originalFinancialKeysForCheck = new Set<string>();
          originalFinancials.forEach(orig => {
            const key = `${orig.id || ''}_${orig.type || ''}_${orig.bank || ''}_${orig.bank_account || ''}`;
            if (key.trim()) {
              originalFinancialKeysForCheck.add(key);
            }
          });
          
          // Create a set of new financials (by id+type+bank+account) to skip field-level change requests for them
          const newFinancialKeys = new Set<string>();
          financials.forEach((fin) => {
            const finKey = `${fin.id || ''}_${fin.type || ''}_${fin.bank || ''}_${fin.bank_account || ''}`;
            if (finKey.trim() && !originalFinancialKeysForCheck.has(finKey)) {
              newFinancialKeys.add(finKey);
            }
          });

          // Build a mapping from actualIndex (in financials array) to originalIndex
          const financialActualIndexToOriginalIndexMap = new Map<number, number>();
          financials.forEach((fin, actualIndex) => {
            const finId = fin.id || (fin as any).id;
            const deletedFinancialIds = new Set(deletedFinancials.map(d => d.data?.id).filter(id => id !== undefined && id !== null));
            if (finId && deletedFinancialIds.has(finId)) {
              return;
            }
            let originalIndex = actualIndex;
            deletedFinancials.forEach(deleted => {
              if (deleted.index <= actualIndex) {
                originalIndex++;
              }
            });
            financialActualIndexToOriginalIndexMap.set(actualIndex, originalIndex);
          });

          for (const [key, newValue] of Object.entries(financialPendingChanges)) {
            const parts = key.split('_');
            if (parts.length < 3) continue;
            const actualIndex = parseInt(parts[1], 10);
            const field = parts.slice(2).join('_') as 'type' | 'bank' | 'branch_name' | 'bank_account';
            
            // Convert actualIndex to originalIndex using the mapping
            const originalIndex = financialActualIndexToOriginalIndexMap.get(actualIndex);
            if (originalIndex === undefined) {
              continue;
            }
            
            // Use originalIndex to find the original financial
            const oldFinancial = originalFinancials[originalIndex];
            if (!oldFinancial || !oldFinancial.id) {
              continue;
            }
            const oldValue = oldFinancial[field]?.toString() || '';
            const fieldName = `financial_${originalIndex}_${field}`;
            if (oldValue !== newValue) {
              changeRequestPromises.push(
                customerApi.post('/customer/business-change-requests', {
                  business_id: businessId,
                  field_name: fieldName,
                  old_value: oldValue,
                  new_value: newValue,
                  status: 'pending',
                })
              );
            }
          }

          // Handle new financials -> create one financial_add request for each new row
          const originalFinancialKeys = new Set<string>();
          originalFinancials.forEach(orig => {
            const key = `${orig.id || ''}_${orig.type || ''}_${orig.bank || ''}_${orig.bank_account || ''}`;
            if (key.trim()) {
              originalFinancialKeys.add(key);
            }
          });
          
          const deletedFinancialIds = new Set(deletedFinancials.map(d => d.data?.id).filter(id => id !== undefined && id !== null));
          
          financials.forEach((fin, currentIndex) => {
            const finId = fin.id || (fin as any).id;
            
            if (finId && deletedFinancialIds.has(finId)) {
              return;
            }
            
            let originalIndex = currentIndex;
            deletedFinancials.forEach(deleted => {
              if (deleted.index <= currentIndex) {
                originalIndex++;
              }
            });
            
            const positionExistsInOriginal = originalFinancials[originalIndex] !== undefined;
            const finKey = `${finId || ''}_${fin.type || ''}_${fin.bank || ''}_${fin.bank_account || ''}`;
            const existsInOriginalByKey = finKey && originalFinancialKeys.has(finKey);
            
            if (!finId && !positionExistsInOriginal && !existsInOriginalByKey) {
              const pendingTypeKey = `financial_${currentIndex}_type`;
              const pendingBankKey = `financial_${currentIndex}_bank`;
              const pendingBranchKey = `financial_${currentIndex}_branch_name`;
              const pendingAccountKey = `financial_${currentIndex}_bank_account`;
              const mergedFinancial = {
                type: financialPendingChanges[pendingTypeKey] ?? fin.type ?? '',
                bank: financialPendingChanges[pendingBankKey] ?? fin.bank ?? '',
                branch_name: financialPendingChanges[pendingBranchKey] ?? fin.branch_name ?? '',
                bank_account: financialPendingChanges[pendingAccountKey] ?? fin.bank_account ?? '',
              };
              
              if (mergedFinancial.type.trim() || mergedFinancial.bank.trim() || mergedFinancial.bank_account.trim()) {
                // Create one financial_add request for each new financial (not an array)
                changeRequestPromises.push(
                  customerApi.post('/customer/business-change-requests', {
                    business_id: businessId,
                    field_name: 'financial_add',
                    old_value: '',
                    new_value: JSON.stringify(mergedFinancial), // Single object, not array
                    status: 'pending',
                  })
                );
              }
            }
          });

          // Handle deleted financials
          deletedFinancials.forEach((item: { index: number; data: any }) => {
            if (item.data?.id) {
              const fieldName = `financial_${item.index}_delete`;
              changeRequestPromises.push(
                customerApi.post('/customer/business-change-requests', {
                  business_id: businessId,
                  field_name: fieldName,
                  old_value: JSON.stringify(item.data),
                  new_value: null,
                  status: 'pending',
                })
              );
            }
          });

          // Handle employer field changes
          const originalEmployer = originalBusiness.employers && originalBusiness.employers.length > 0 ? originalBusiness.employers[0] : null;
          if (originalEmployer) {
            const employerFields: Array<keyof CreateBusinessEmployerData> = [
              'employer_no',
              'employer_no_password',
              'employer_account_no',
              'sst_id',
              'sst_password',
              'sst_account_no',
              'kwsp_id',
              'kwsp_password',
              'kwsp_account_no',
              'perkeso_id',
              'perkeso_password',
              'perkeso_account_no',
              'hrdc_id',
              'hrdc_password',
              'hrdc_account_no',
              'pcb',
            ];
            employerFields.forEach((field) => {
              const key = `employer_${field}`;
              if (employerPendingChanges[key] !== undefined) {
                const newValue = employerPendingChanges[key];
                const oldValue = (originalEmployer[field] as any)?.toString() || '';
                console.log(`🔍 [BusinessForm] Employer field ${field}: old="${oldValue}", new="${newValue}"`);
                if (oldValue !== (newValue as any)?.toString()) {
                  changeRequestPromises.push(
                    customerApi.post('/customer/business-change-requests', {
                      business_id: businessId,
                      field_name: key,
                      old_value: oldValue,
                      new_value: newValue,
                      status: 'pending',
                    })
                  );
                }
              }
            });
          } else if (
            employer.employer_no ||
            employer.employer_no_password ||
            employer.employer_account_no ||
            employer.sst_id ||
            employer.sst_password ||
            employer.sst_account_no ||
            employer.kwsp_id ||
            employer.kwsp_password ||
            employer.kwsp_account_no ||
            employer.perkeso_id ||
            employer.perkeso_password ||
            employer.perkeso_account_no ||
            employer.hrdc_id ||
            employer.hrdc_password ||
            employer.hrdc_account_no ||
            employer.pcb
          ) {
            // New employer - create add request
            console.log('🔍 [BusinessForm] Creating new employer add request:', employer);
            changeRequestPromises.push(
              customerApi.post('/customer/business-change-requests', {
                business_id: businessId,
                field_name: 'employer_add',
                old_value: null,
                new_value: JSON.stringify(employer),
                status: 'pending',
              })
            );
          }

          // Handle Directors field changes
          const originalDirectors: CreateBusinessDirectorData[] = [];
          if (originalBusiness.directors && originalBusiness.directors.length > 0) {
            originalBusiness.directors.forEach((dir: any) => {
              originalDirectors.push({
                id: dir.id,
                customer_id: dir.customer_id || undefined,
                name: dir.name || '',
                identification_no: dir.identification_no || '',
                appoint_date: dir.appoint_date || '',
                resign_date: dir.resign_date || '',
                position: dir.position || '',
              });
            });
          }

          const directorActualToOriginal = new Map<number, number>();
          directors.forEach((dir, actualIndex) => {
            const dirId = dir.id || (dir as any).id;
            const deletedIds = new Set(deletedDirectors.map(d => d.data?.id).filter(id => id !== undefined && id !== null));
            if (dirId && deletedIds.has(dirId)) return;
            let originalIndex = actualIndex;
            deletedDirectors.forEach(d => {
              if (d.index <= actualIndex) originalIndex++;
            });
            directorActualToOriginal.set(actualIndex, originalIndex);
          });

          for (const [key, newValue] of Object.entries(directorPendingChanges)) {
            const parts = key.split('_');
            if (parts.length < 3) continue;
            const actualIndex = parseInt(parts[1], 10);
            const field = parts.slice(2).join('_') as keyof CreateBusinessDirectorData;
            const originalIndex = directorActualToOriginal.get(actualIndex);
            if (originalIndex === undefined) continue;
            const oldDirector = originalDirectors[originalIndex];
            if (!oldDirector || !oldDirector.id) continue;
            let oldValue = (oldDirector[field] as any);
            let newValueNormalized: any = newValue;
            if (field === 'customer_id') {
              oldValue = oldValue ? String(oldValue) : '';
              newValueNormalized = newValue ? String(newValue) : '';
            }
            const oldValueString = (oldValue ?? '').toString();
            const fieldName = `director_${originalIndex}_${field}`;
            if (oldValueString !== (newValueNormalized ?? '').toString()) {
              changeRequestPromises.push(
                customerApi.post('/customer/business-change-requests', {
                  business_id: businessId,
                  field_name: fieldName,
                  old_value: oldValueString,
                  new_value: (newValueNormalized ?? '').toString(),
                  status: 'pending',
                })
              );
            }
          }

          // Handle new directors -> create one director_add request for each new row
          const originalDirectorKeys = new Set<string>();
          originalDirectors.forEach(orig => {
            const key = `${orig.id || ''}_${orig.customer_id || ''}_${orig.name || ''}_${orig.identification_no || ''}`;
            if (key.trim()) originalDirectorKeys.add(key);
          });

          const deletedDirectorIds = new Set(deletedDirectors.map(d => d.data?.id).filter(id => id !== undefined && id !== null));

          directors.forEach((dir, currentIndex) => {
            const dirId = dir.id || (dir as any).id;
            if (dirId && deletedDirectorIds.has(dirId)) return;

            let originalIndex = currentIndex;
            deletedDirectors.forEach(d => {
              if (d.index <= currentIndex) originalIndex++;
            });

            const positionExistsInOriginal = originalDirectors[originalIndex] !== undefined;
            const dirKey = `${dirId || ''}_${dir.customer_id || ''}_${dir.name || ''}_${dir.identification_no || ''}`;
            const existsInOriginalByKey = dirKey && originalDirectorKeys.has(dirKey);

            if (!dirId && !positionExistsInOriginal && !existsInOriginalByKey) {
              const pendingCustomerKey = `director_${currentIndex}_customer_id`;
              const pendingNameKey = `director_${currentIndex}_name`;
              const pendingIdKey = `director_${currentIndex}_identification_no`;
              const pendingPosKey = `director_${currentIndex}_position`;
              const pendingAppKey = `director_${currentIndex}_appoint_date`;
              const pendingResKey = `director_${currentIndex}_resign_date`;
              const merged = {
                customer_id: directorPendingChanges[pendingCustomerKey] ? parseInt(directorPendingChanges[pendingCustomerKey], 10) : (dir.customer_id || undefined),
                name: directorPendingChanges[pendingNameKey] ?? dir.name ?? '',
                identification_no: directorPendingChanges[pendingIdKey] ?? dir.identification_no ?? '',
                position: directorPendingChanges[pendingPosKey] ?? dir.position ?? '',
                appoint_date: directorPendingChanges[pendingAppKey] ?? dir.appoint_date ?? '',
                resign_date: directorPendingChanges[pendingResKey] ?? dir.resign_date ?? '',
              };
              console.log('🔍 [BusinessForm] New director merged data:', merged);
              if (merged.name.trim() || merged.identification_no.trim() || merged.position.trim() || merged.appoint_date.trim() || merged.resign_date.trim()) {
                // Create one director_add request for each new director (not an array)
                changeRequestPromises.push(
                  customerApi.post('/customer/business-change-requests', {
                    business_id: businessId,
                    field_name: 'director_add',
                    old_value: '',
                    new_value: JSON.stringify(merged), // Single object, not array
                    status: 'pending',
                  })
                );
              }
            }
          });

          // Handle deleted directors
          deletedDirectors.forEach((item: { index: number; data: any }) => {
            if (item.data?.id) {
              const fieldName = `director_${item.index}_delete`;
              changeRequestPromises.push(
                customerApi.post('/customer/business-change-requests', {
                  business_id: businessId,
                  field_name: fieldName,
                  old_value: JSON.stringify(item.data),
                  new_value: null,
                  status: 'pending',
                })
              );
            }
          });

          // Handle deleted members
          deletedMembers.forEach((item: { index: number; data: any }) => {
            if (item.data?.id) {
              const fieldName = `member_${item.index}_delete`;
              changeRequestPromises.push(
                customerApi.post('/customer/business-change-requests', {
                  business_id: businessId,
                  field_name: fieldName,
                  old_value: JSON.stringify(item.data),
                  new_value: null,
                  status: 'pending',
                })
              );
            }
          });

          // Handle Members field changes
          const originalMembers: CreateBusinessMemberData[] = [];
          if (originalBusiness.members && originalBusiness.members.length > 0) {
            originalBusiness.members.forEach((mem: any) => {
              originalMembers.push({
                id: mem.id,
                name: mem.name || '',
                identification_no: mem.identification_no || '',
                appoint_date: mem.appoint_date || '',
                resign_date: mem.resign_date || '',
                share: mem.share || 0,
              });
            });
          }

          const memberActualToOriginal = new Map<number, number>();
          members.forEach((mem, actualIndex) => {
            const memId = mem.id || (mem as any).id;
            const deletedIds = new Set(deletedMembers.map(d => d.data?.id).filter(id => id !== undefined && id !== null));
            if (memId && deletedIds.has(memId)) return;
            let originalIndex = actualIndex;
            deletedMembers.forEach(d => {
              if (d.index <= actualIndex) originalIndex++;
            });
            memberActualToOriginal.set(actualIndex, originalIndex);
          });

          for (const [key, newValue] of Object.entries(memberPendingChanges)) {
            const parts = key.split('_');
            if (parts.length < 3) continue;
            const actualIndex = parseInt(parts[1], 10);
            const field = parts.slice(2).join('_') as keyof CreateBusinessMemberData;
            const originalIndex = memberActualToOriginal.get(actualIndex);
            if (originalIndex === undefined) continue;
            const oldMember = originalMembers[originalIndex];
            if (!oldMember || !oldMember.id) continue;
            const oldValue = (oldMember[field] as any)?.toString() || '';
            const fieldName = `member_${originalIndex}_${field}`;
            if (oldValue !== (newValue as any)?.toString()) {
              changeRequestPromises.push(
                customerApi.post('/customer/business-change-requests', {
                  business_id: businessId,
                  field_name: fieldName,
                  old_value: oldValue,
                  new_value: newValue,
                  status: 'pending',
                })
              );
            }
          }

          // Handle new members -> create one member_add request for each new row
          const originalMemberKeys = new Set<string>();
          originalMembers.forEach(orig => {
            const key = `${orig.id || ''}_${orig.name || ''}_${orig.identification_no || ''}`;
            if (key.trim()) originalMemberKeys.add(key);
          });

          const deletedMemberIds = new Set(deletedMembers.map(d => d.data?.id).filter(id => id !== undefined && id !== null));

          members.forEach((mem, currentIndex) => {
            const memId = mem.id || (mem as any).id;
            if (memId && deletedMemberIds.has(memId)) return;

            let originalIndex = currentIndex;
            deletedMembers.forEach(d => {
              if (d.index <= currentIndex) originalIndex++;
            });

            const positionExistsInOriginal = originalMembers[originalIndex] !== undefined;
            const memKey = `${memId || ''}_${mem.name || ''}_${mem.identification_no || ''}`;
            const existsInOriginalByKey = memKey && originalMemberKeys.has(memKey);

            if (!memId && !positionExistsInOriginal && !existsInOriginalByKey) {
              const pendingNameKey = `member_${currentIndex}_name`;
              const pendingIdKey = `member_${currentIndex}_identification_no`;
              const pendingShareKey = `member_${currentIndex}_share`;
              const pendingAppKey = `member_${currentIndex}_appoint_date`;
              const pendingResKey = `member_${currentIndex}_resign_date`;
              const merged = {
                name: memberPendingChanges[pendingNameKey] ?? mem.name ?? '',
                identification_no: memberPendingChanges[pendingIdKey] ?? mem.identification_no ?? '',
                share: Number(memberPendingChanges[pendingShareKey] ?? mem.share ?? 0),
                appoint_date: memberPendingChanges[pendingAppKey] ?? mem.appoint_date ?? '',
                resign_date: memberPendingChanges[pendingResKey] ?? mem.resign_date ?? '',
              };
              const hasVal =
                merged.name.trim() ||
                merged.identification_no.trim() ||
                merged.share !== 0 ||
                merged.appoint_date.trim() ||
                merged.resign_date.trim();
              if (hasVal) {
                // Create one member_add request for each new member (not an array)
                changeRequestPromises.push(
                  customerApi.post('/customer/business-change-requests', {
                    business_id: businessId,
                    field_name: 'member_add',
                    old_value: '',
                    new_value: JSON.stringify(merged), // Single object, not array
                    status: 'pending',
                  })
                );
              }
            }
          });

          if (changeRequestPromises.length > 0) {
            await Promise.all(changeRequestPromises);
            message.success('Change requests submitted successfully');
            
            // Clear pending changes after successful submission
            setPendingChanges({});
            setAddressPendingChanges({});
            setDeletedAddresses([]);
            setMsicCodePendingChanges({});
            setDeletedMsicCodes([]);
            setFinancialPendingChanges({});
            setDeletedFinancials([]);
            setDirectorPendingChanges({});
            setDeletedDirectors([]);
            setEmployerPendingChanges({});
            setMemberPendingChanges({});
            setDeletedMembers([]);
            // Reload page after save
            window.location.reload();
          } else {
            message.info('No changes to submit');
            // Exit edit mode if no changes (similar to CustomerPersonalDetail)
            setIsEditing(false);
            setSubmitting(false);
            return; // Exit early if no changes
          }
        } else {
          // Admin panel: update business
          // Check if there are any files to upload (supporting files or engagement files)
          const removedSupportingFields = Object.keys(removedSupportingFiles).filter(
            (field) => removedSupportingFiles[field]
          );
          const hasSupportingFileDeletes = removedSupportingFields.length > 0;
          const hasSupportingFiles = ['name', 'sst_no', 'tin_no', 'trademark_logo', 'industry_specific_license', 'company_chop'].some(field => {
            const files = supportingFiles[field as keyof typeof supportingFiles];
            return files && files.length > 0 && files[0].originFileObj;
          });
          const hasEngagementFiles = ['account_attachment', 'tax_attachment', 'audit_attachment', 'secretary_attachment'].some(field => {
            const files = engagementFiles[field as keyof typeof engagementFiles];
            return files && files.length > 0 && files[0].originFileObj;
          });
          const hasAddressFiles = Object.values(addressFiles).some(
            (files) => files && files.length > 0 && files[0].originFileObj
          );
          const hasFiles = hasSupportingFiles || hasEngagementFiles || hasAddressFiles || hasSupportingFileDeletes;
          
          if (hasFiles) {
            // Handle supporting file uploads (name, sst_no, tin_no, company_chop) using FormData
            const formData = new FormData();
            Object.entries(submitData).forEach(([key, value]) => {
              if (key === 'addresses' && Array.isArray(value)) {
                return;
              }
              if (value !== undefined && value !== null) {
                if (key === 'supporting' && typeof value === 'object' && !Array.isArray(value)) {
                  const supportingValue = value as Record<string, string>;
                  if (supportingValue.former_name_text) {
                    formData.append('supporting[former_name_text]', supportingValue.former_name_text);
                  }
                  return;
                }
                if (typeof value === 'boolean') {
                  formData.append(key, value ? '1' : '0');
                } else if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof File)) {
                  formData.append(key, JSON.stringify(value));
                } else if (Array.isArray(value)) {
                  value.forEach((item, index) => {
                    if (typeof item === 'object') {
                      Object.entries(item).forEach(([itemKey, itemValue]) => {
                        if (itemValue !== undefined && itemValue !== null) {
                          if (typeof itemValue === 'boolean') {
                            formData.append(`${key}[${index}][${itemKey}]`, itemValue ? '1' : '0');
                          } else {
                            formData.append(`${key}[${index}][${itemKey}]`, itemValue.toString());
                          }
                        }
                      });
                    } else {
                      formData.append(`${key}[${index}]`, item.toString());
                    }
                  });
                } else {
                  formData.append(key, value.toString());
                }
              }
            });
            
            // Add supporting files (only new files with originFileObj)
            const supportingFileFields = ['name', 'sst_no', 'tin_no', 'trademark_logo', 'industry_specific_license', 'company_chop'] as const;
            supportingFileFields.forEach(field => {
              const files = supportingFiles[field];
              if (files && files.length > 0 && files[0].originFileObj) {
                formData.append(`supporting_${field}`, files[0].originFileObj);
              }
            });
            removedSupportingFields.forEach((field) => {
              formData.append(`supporting_${field}`, '');
            });
            
            // Add engagement files (only new files with originFileObj)
            const engagementFileFields = ['account_attachment', 'tax_attachment', 'audit_attachment', 'secretary_attachment'] as const;
            engagementFileFields.forEach(field => {
              const files = engagementFiles[field];
              if (files && files.length > 0 && files[0].originFileObj) {
                formData.append(field, files[0].originFileObj);
              }
            });

            // Add addresses (including attachments)
            formattedAddresses.forEach((address, index) => {
              Object.entries(address).forEach(([fieldKey, fieldValue]) => {
                if (fieldValue !== undefined && fieldValue !== null && fieldKey !== 'attachment') {
                  formData.append(`addresses[${index}][${fieldKey}]`, fieldValue.toString());
                }
              });
              const files = addressFiles[`address_${index}`];
              if (files && files.length > 0 && files[0].originFileObj) {
                formData.append(`addresses[${index}][attachment]`, files[0].originFileObj);
              } else if (address.attachment) {
                formData.append(`addresses[${index}][attachment]`, address.attachment as string);
              }
            });
            
            await businessService.update(parseInt(id, 10), formData);
          } else {
            // No files to upload, use regular update
            const result = await businessService.update(parseInt(id, 10), submitData);
          }
          
          // Process pending change request approvals and rejections
          if (pendingApprovals.length > 0 || pendingRejections.length > 0) {
            try {
              // Process approvals
              for (const requestId of pendingApprovals) {
                await businessService.approveChangeRequest(requestId);
              }
              
              // Process rejections
              for (const requestId of pendingRejections) {
                await businessService.rejectChangeRequest(requestId);
              }
              
              // Clear pending lists
              setPendingApprovals([]);
              setPendingRejections([]);
            } catch (error) {
              console.error('Error processing change requests:', error);
              message.warning('Business updated, but some change requests could not be processed');
            }
          }
          
        setSubmitNotice({ type: 'success', message: 'Business updated successfully.' });
          // Reload business data to refresh file states (similar to CustomerForm)
          await loadBusiness(parseInt(id, 10));
          setIsEditing(false);
        }
      } else {
        // Creating new business
        // Check if there are any files to upload (supporting files or engagement files)
        const removedSupportingFields = Object.keys(removedSupportingFiles).filter(
          (field) => removedSupportingFiles[field]
        );
        const hasSupportingFileDeletes = removedSupportingFields.length > 0;
        const hasSupportingFiles = ['name', 'sst_no', 'tin_no', 'trademark_logo', 'industry_specific_license', 'company_chop'].some(field => {
          const files = supportingFiles[field as keyof typeof supportingFiles];
          return files && files.length > 0 && files[0].originFileObj;
        });
        const hasEngagementFiles = ['account_attachment', 'tax_attachment', 'audit_attachment', 'secretary_attachment'].some(field => {
          const files = engagementFiles[field as keyof typeof engagementFiles];
          return files && files.length > 0 && files[0].originFileObj;
        });
        const hasAddressFiles = Object.values(addressFiles).some(
          (files) => files && files.length > 0 && files[0].originFileObj
        );
        const hasFiles = hasSupportingFiles || hasEngagementFiles || hasAddressFiles || hasSupportingFileDeletes;
        
        if (hasFiles) {
          // Handle supporting file uploads using FormData
          const formData = new FormData();
          Object.entries(submitData).forEach(([key, value]) => {
            if (key === 'addresses' && Array.isArray(value)) {
              return;
            }
            if (value !== undefined && value !== null) {
              if (key === 'supporting' && typeof value === 'object' && !Array.isArray(value)) {
                const supportingValue = value as Record<string, string>;
                if (supportingValue.former_name_text) {
                  formData.append('supporting[former_name_text]', supportingValue.former_name_text);
                }
                return;
              }
              if (typeof value === 'boolean') {
                formData.append(key, value ? '1' : '0');
              } else if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof File)) {
                formData.append(key, JSON.stringify(value));
              } else if (Array.isArray(value)) {
                value.forEach((item, index) => {
                  if (typeof item === 'object') {
                    Object.entries(item).forEach(([itemKey, itemValue]) => {
                      if (itemValue !== undefined && itemValue !== null) {
                        if (typeof itemValue === 'boolean') {
                          formData.append(`${key}[${index}][${itemKey}]`, itemValue ? '1' : '0');
                        } else {
                          formData.append(`${key}[${index}][${itemKey}]`, itemValue.toString());
                        }
                      }
                    });
                  } else {
                    formData.append(`${key}[${index}]`, item.toString());
                  }
                });
              } else {
                formData.append(key, value.toString());
              }
            }
          });
          
          // Add supporting files (only new files with originFileObj)
          const supportingFileFields = ['name', 'sst_no', 'tin_no', 'trademark_logo', 'industry_specific_license', 'company_chop'] as const;
          supportingFileFields.forEach(field => {
            const files = supportingFiles[field];
            if (files && files.length > 0 && files[0].originFileObj) {
              formData.append(`supporting_${field}`, files[0].originFileObj);
            }
          });
          removedSupportingFields.forEach((field) => {
            formData.append(`supporting_${field}`, '');
          });
          
          // Add engagement files (only new files with originFileObj)
          const engagementFileFields = ['account_attachment', 'tax_attachment', 'audit_attachment', 'secretary_attachment'] as const;
          engagementFileFields.forEach(field => {
            const files = engagementFiles[field];
            if (files && files.length > 0 && files[0].originFileObj) {
              formData.append(field, files[0].originFileObj);
            }
          });

          // Add addresses (including attachments)
          formattedAddresses.forEach((address, index) => {
            Object.entries(address).forEach(([fieldKey, fieldValue]) => {
              if (fieldValue !== undefined && fieldValue !== null && fieldKey !== 'attachment') {
                formData.append(`addresses[${index}][${fieldKey}]`, fieldValue.toString());
              }
            });
            const files = addressFiles[`address_${index}`];
            if (files && files.length > 0 && files[0].originFileObj) {
              formData.append(`addresses[${index}][attachment]`, files[0].originFileObj);
            } else if (address.attachment) {
              formData.append(`addresses[${index}][attachment]`, address.attachment as string);
            }
          });
          
          if (isCustomerPortalPage) {
            await customerApi.post('/customer/businesses', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });
          } else {
            await api.post('/businesses', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });
          }
        } else {
        if (isCustomerPortalPage) {
          await customerApi.post('/customer/businesses', submitData);
        } else {
          await businessService.create(submitData);
          }
        }
        const notice = { type: 'success' as const, message: 'Business created successfully.' };
        // For new business, navigate away (no need to reload)
        navigate(isCustomerPortalPage ? '/customer/personal' : '/businesses', { state: { notice } });
      }
    } catch (error: any) {
      console.error('Business submit error:', error);
      
      // Handle validation errors (422)
      if (error.response?.status === 422 && error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const errorMessages: string[] = [];
        const formErrors: { [key: string]: { errors: string[] } } = {};
        
        // Collect all error messages and set form field errors
        Object.keys(errors).forEach((field) => {
          const fieldErrors = errors[field];
          const errorArray = Array.isArray(fieldErrors) ? fieldErrors : [fieldErrors];
          
          // Set form field errors
          formErrors[field] = { errors: errorArray };
          
          // Collect messages for notification
          errorArray.forEach((err: string) => {
            errorMessages.push(`${field}: ${err}`);
          });
        });
        
        // Set errors on form fields
        Object.keys(formErrors).forEach((field) => {
          const fieldName = field.split('.');
          form.setFields([{
            name: fieldName as any,
            errors: formErrors[field].errors,
          }]);
        });
        
        // Show notification with all validation errors
        setSubmitNotice({
          type: 'failure',
          message: errorMessages.length > 0
            ? errorMessages.join('\n')
            : error.response?.data?.message || 'Please check the form for errors.',
        });
      } else {
        // Handle other errors
        const errorMessage = error.response?.data?.message || 
                            error.message || 
                            (isEdit ? 'Failed to update business' : 'Failed to create business');
        setSubmitNotice({
          type: 'failure',
          message: errorMessage,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const addAddress = () => {
    // Ignore any event parameter that might be passed
    const newAddress: CreateBusinessAddressData = { 
      type: 'main',
      address1: '', 
      address2: '', 
    };
    const newAddresses = [...addresses, newAddress];
    setAddresses(newAddresses);
  };

  const removeAddress = (index: number) => {
    const address = addresses[index];
    if (address && (address as any).id) {
      // Mark for deletion (will create change request on save)
      setDeletedAddresses(prev => [...prev, { index, data: address }]);
      message.info('Address will be deleted on save');
    }
    setAddresses(addresses.filter((_, i) => i !== index));
  };

  const markAddressForDeletion = (index: number) => {
    const address = addresses[index];
    if (address && (address as any).id) {
      setDeletedAddresses(prev => [...prev, { index, data: address }]);
      message.info('Address will be deleted on save');
    }
    setAddresses(addresses.filter((_, i) => i !== index));
  };

  const updateAddress = (index: number, field: keyof CreateBusinessAddressData, value: string) => {
    const updated = [...addresses];
    // Validate type field - only allow 'main' or 'branch'
    if (field === 'type' && value !== 'main' && value !== 'branch') {
      return;
    }
    updated[index] = { ...updated[index], [field]: value };
    setAddresses(updated);
  };

  const handleAddressFieldChange = (index: number, field: keyof CreateBusinessAddressData, value: string) => {
    // Validate type field - only allow 'main' or 'branch'
    if (field === 'type' && value !== 'main' && value !== 'branch') {
      return;
    }
    const key = `address_${index}_${field}`;
    setAddressPendingChanges(prev => ({
      ...prev,
      [key]: value,
    }));
    // Always update local state for immediate UI feedback and to ensure data is available on submit
    const updated = [...addresses];
    if (updated[index]) {
      updated[index] = { ...updated[index], [field]: value };
      setAddresses(updated);
    } else {
      // If address doesn't exist, create it
      const newAddress: CreateBusinessAddressData = {
        type: 'main',
        address1: '',
        address2: '',
        [field]: value,
      };
      // Ensure the array is long enough
      while (updated.length <= index) {
        updated.push({ type: 'main', address1: '', address2: '' });
      }
      updated[index] = newAddress;
      setAddresses(updated);
    }
  };

  const getAddressDisplayValue = (index: number, field: keyof CreateBusinessAddressData): string => {
    const key = `address_${index}_${field}`;
    return addressPendingChanges[key] || '';
  };

  const addMsicCode = (msicData?: { code: string; description: string }) => {
    if (msicCodes.length < 5) {
      setMsicCodes([...msicCodes, msicData || { code: '', description: '' }]);
    } else {
      message.warning('Maximum 5 MSIC codes allowed');
    }
  };

  const removeMsicCode = (index: number) => {
    const msicToRemove = msicCodes[index];
    
    // MSIC codes don't have independent ids, but we check if it exists in original data
    // For now, we'll mark all deletions for change requests in customer portal
    if ((isCustomerPortalPage || (isEdit && isSeniorOrJunior)) && msicToRemove) {
      // Mark as deleted (will be shown with strikethrough)
      setDeletedMsicCodes(prev => [...prev, { index, data: msicToRemove }]);
      
      // Remove from local state to avoid duplicate display
      setMsicCodes(msicCodes.filter((_, i) => i !== index));
      
      // Create change request for deletion (will be submitted on Save)
      message.info('Delete request will be submitted when you click Save');
      return;
    }
    
    // If admin portal or new code, just remove from local state
    setMsicCodes(msicCodes.filter((_, i) => i !== index));
  };

  const updateMsicCode = (index: number, field: 'code' | 'description', value: string) => {
    if (isCustomerPortalPage) {
    const updated = [...msicCodes];
      if (updated[index]) {
    updated[index] = { ...updated[index], [field]: value };
    setMsicCodes(updated);
      }
    } else {
      const updated = [...msicCodes];
      updated[index] = { ...updated[index], [field]: value };
      setMsicCodes(updated);
    }
  };

  const handleMsicCodeFieldChange = (index: number, field: 'code' | 'description', value: string) => {
    if (isCustomerPortalPage || (isEdit && isSeniorOrJunior)) {
      const key = `msic_${index}_${field}`;
      setMsicCodePendingChanges(prev => ({
        ...prev,
        [key]: value,
      }));
      // Also update local state for immediate UI feedback
      // Only update if the index exists in msicCodes array (don't auto-create rows)
      const updated = [...msicCodes];
      if (updated[index]) {
        updated[index] = { ...updated[index], [field]: value };
        setMsicCodes(updated);
      }
      // If index doesn't exist, don't update msicCodes (this shouldn't happen with correct actualIndex)
    } else {
      // Admin portal: directly update the MSIC code
      updateMsicCode(index, field, value);
    }
  };

  const getMsicCodeDisplayValue = (index: number, field: 'code' | 'description'): string => {
    const key = `msic_${index}_${field}`;
    return msicCodePendingChanges[key] || '';
  };

  const markMsicCodeForDeletion = (index: number) => {
    const msic = msicCodes[index];
    if (msic) {
      setDeletedMsicCodes(prev => [...prev, { index, data: msic }]);
      message.info('MSIC code will be deleted on save');
    }
    setMsicCodes(msicCodes.filter((_, i) => i !== index));
  };

  const addFinancial = (financialData?: CreateBusinessFinancialData) => {
    setFinancials([...financials, financialData || { type: '', bank: '', branch_name: '', bank_account: '' }]);
  };

  const removeFinancial = (index: number) => {
    const financial = financials[index];
    if (financial && (financial as any).id) {
      // Mark for deletion (will create change request on save)
      setDeletedFinancials(prev => [...prev, { index, data: financial }]);
      if (isCustomerPortalPage) {
        message.info('Financial will be deleted on save');
      }
    }
    // Remove from local state
    setFinancials(financials.filter((_, i) => i !== index));
  };

  const updateFinancial = (index: number, field: keyof CreateBusinessFinancialData, value: string) => {
    if (isCustomerPortalPage) {
      // For customer portal, track pending changes
      const key = `financial_${index}_${field}`;
      setFinancialPendingChanges(prev => ({
        ...prev,
        [key]: value,
      }));
    }
    // Also update local state for immediate UI feedback
    const updated = [...financials];
    updated[index] = { ...updated[index], [field]: value };
    setFinancials(updated);
  };

  const handleFinancialFieldChange = (index: number, field: 'type' | 'bank' | 'branch_name' | 'bank_account', value: string) => {
    if (isCustomerPortalPage || (isEdit && isSeniorOrJunior)) {
      const key = `financial_${index}_${field}`;
      setFinancialPendingChanges(prev => ({
        ...prev,
        [key]: value,
      }));
      // Also update local state for immediate UI feedback
      const updated = [...financials];
      if (updated[index]) {
        updated[index] = { ...updated[index], [field]: value };
        setFinancials(updated);
      }
    } else {
      // Admin portal: directly update the financial
      updateFinancial(index, field, value);
    }
  };

  const getFinancialDisplayValue = (index: number, field: 'type' | 'bank' | 'branch_name' | 'bank_account'): string => {
    const key = `financial_${index}_${field}`;
    return financialPendingChanges[key] || '';
  };

  const updateEmployer = (field: keyof CreateBusinessEmployerData, value: string) => {
    if (isCustomerPortalPage) {
      const key = `employer_${field}`;
      setEmployerPendingChanges(prev => ({
        ...prev,
        [key]: value,
      }));
    }
    setEmployer({ ...employer, [field]: value });
  };
  const handleEmployerFieldChange = (field: keyof CreateBusinessEmployerData, value: string) => {
    const key = `employer_${field}`;
    if (isCustomerPortalPage || (isEdit && isSeniorOrJunior)) {
      setEmployerPendingChanges(prev => ({
        ...prev,
        [key]: value,
      }));
    }
    setEmployer({ ...employer, [field]: value });
  };

  const getEmployerDisplayValue = (field: keyof CreateBusinessEmployerData): string => {
    const key = `employer_${field}`;
    return employerPendingChanges[key] || '';
  };

  const addDirector = () => {
    setDirectors([...directors, {
      customer_id: undefined,
      name: '',
      identification_no: '',
      appoint_date: '',
      resign_date: '',
      position: '',
    }]);
  };

  const removeDirector = (index: number) => {
    const director = directors[index];
    if (director && (director as any).id) {
      if (isCustomerPortalPage || (isEdit && isSeniorOrJunior)) {
        setDeletedDirectors(prev => [...prev, { index, data: director }]);
        message.info('Director will be deleted on save');
      }
    }
    setDirectors(directors.filter((_, i) => i !== index));
  };

  const updateDirector = (index: number, field: keyof CreateBusinessDirectorData, value: string | number | undefined) => {
    const normalizedValue = normalizeDirectorMemberDate(field, value);
    if (isCustomerPortalPage) {
      const key = `director_${index}_${field}`;
      setDirectorPendingChanges(prev => ({
        ...prev,
        [key]: normalizedValue as any,
      }));
    }
    // Use functional update to ensure we get the latest state
    setDirectors(prev => {
      const updated = [...prev];
      if (updated[index]) {
    updated[index] = { ...updated[index], [field]: normalizedValue };
        return updated;
      }
      return prev;
    });
  };

  const handleDirectorFieldChange = (index: number, field: keyof CreateBusinessDirectorData, value: string | number | undefined) => {
    const normalizedValue = normalizeDirectorMemberDate(field, value);
    const key = `director_${index}_${field}`;
    setDirectorPendingChanges(prev => ({
      ...prev,
      [key]: normalizedValue as any,
    }));
    // Use functional update to ensure we get the latest state
    setDirectors(prev => {
      const updated = [...prev];
    if (updated[index]) {
      updated[index] = { ...updated[index], [field]: normalizedValue };
        return updated;
    }
      return prev;
    });
  };

  const getDirectorDisplayValue = (index: number, field: keyof CreateBusinessDirectorData): string => {
    const key = `director_${index}_${field}`;
    return (directorPendingChanges[key] as string) || '';
  };

  const addMember = () => {
    setMembers([...members, { 
      type: 'customer',
      reference_id: undefined,
      customer_id: undefined,
      name: '',
      identification_no: '',
      appoint_date: '',
      resign_date: '',
      share: 0,
    }]);
  };

  const removeMember = (index: number) => {
    const member = members[index];
    if (member && (member as any).id) {
      if (isCustomerPortalPage || (isEdit && isSeniorOrJunior)) {
        setDeletedMembers(prev => [...prev, { index, data: member }]);
        message.info('Member will be deleted on save');
      }
    }
    setMembers(members.filter((_, i) => i !== index));
  };

  const updateMember = (index: number, field: keyof CreateBusinessMemberData, value: string | number | undefined) => {
    const normalizedValue = normalizeDirectorMemberDate(field, value);
    if (isCustomerPortalPage) {
      const key = `member_${index}_${field}`;
      setMemberPendingChanges(prev => ({
        ...prev,
        [key]: normalizedValue as any,
      }));
    }
    // Use functional update to ensure we get the latest state
    setMembers(prev => {
      const updated = [...prev];
      if (updated[index]) {
    updated[index] = { ...updated[index], [field]: normalizedValue };
        return updated;
      }
      return prev;
    });
  };

  const handleMemberFieldChange = (index: number, field: keyof CreateBusinessMemberData, value: string | number | undefined) => {
    const normalizedValue = normalizeDirectorMemberDate(field, value);
    const key = `member_${index}_${field}`;
    setMemberPendingChanges(prev => ({
      ...prev,
      [key]: normalizedValue as any,
    }));
    // Use functional update to ensure we get the latest state
    setMembers(prev => {
      const updated = [...prev];
    if (updated[index]) {
      updated[index] = { ...updated[index], [field]: normalizedValue };
        return updated;
    }
      return prev;
    });
  };

  const getMemberDisplayValue = (index: number, field: keyof CreateBusinessMemberData): string => {
    const key = `member_${index}_${field}`;
    return (memberPendingChanges[key] as string) || '';
  };


  // Change request handlers
  const getChangeRequestForField = (fieldName: string): any | undefined => {
    return changeRequests.find(cr => cr.field_name === fieldName && cr.status === 'pending');
  };

  const handleApproveChangeRequest = (requestId: number) => {
    const changeRequest = changeRequests.find(cr => cr.id === requestId);
    if (!changeRequest) return;
    
    const fieldName = changeRequest.field_name;
    const newValue = changeRequest.new_value;
    
    // Immediately update UI to show approved status
    setChangeRequests(prev => prev.map(cr => 
      cr.id === requestId 
        ? { ...cr, status: 'approved' as const, reviewed_at: new Date().toISOString() }
        : cr
    ));
    
    // Handle different types of change requests
    if (fieldName?.endsWith('_delete')) {
      // Handle delete requests: mark item for deletion in UI
      // Note: The index in field_name is the originalIndex, not the current array index
      // We need to find the item by matching data, not by index
      const match = fieldName.match(/^(msic|address|financial|director|member)_(\d+)_delete$/);
      if (match) {
        const [, type, indexStr] = match;
        const originalIndex = parseInt(indexStr, 10);
        
        if (type === 'msic') {
          // For MSIC codes, calculate which item in current array corresponds to originalIndex
          // originalIndex accounts for items that were deleted before this position
          // So we need to find the item at position: originalIndex - number of deleted items before this position
          let currentIndex = originalIndex;
          deletedMsicCodes.forEach(deleted => {
            if (deleted.index < originalIndex) {
              currentIndex--;
            }
          });
          
          // Find the MSIC code at the calculated currentIndex
          const msicToDelete = msicCodes[currentIndex];
          if (msicToDelete) {
            // Only mark for deletion, don't remove from array yet (will be processed on Save)
            setDeletedMsicCodes(prev => {
              // Check if already marked
              const exists = prev.some(d => d.index === originalIndex);
              if (exists) return prev;
              return [...prev, { index: originalIndex, data: msicToDelete }];
            });
          }
        } else if (type === 'address') {
          // For addresses, find by id from old_value (which is JSON stringified data)
          try {
            const oldData = changeRequest.old_value ? JSON.parse(changeRequest.old_value) : null;
            const targetId = oldData?.id || oldData?._contactId;
            if (targetId) {
              const addressToDelete = addresses.find((addr) => {
                const addrId = (addr as any).id || (addr as any)._contactId;
                return addrId && addrId.toString() === targetId.toString();
              });
              if (addressToDelete) {
                // Only mark for deletion, don't remove from array yet (will be processed on Save)
                setDeletedAddresses(prev => {
                  const exists = prev.some(d => d.index === originalIndex);
                  if (exists) return prev;
                  return [...prev, { index: originalIndex, data: addressToDelete }];
                });
              }
            }
          } catch (e) {
            console.error('Error parsing address delete old_value:', e);
          }
        } else if (type === 'financial') {
          try {
            const oldData = changeRequest.old_value ? JSON.parse(changeRequest.old_value) : null;
            const targetId = oldData?.id;
            if (targetId) {
              const financialToDelete = financials.find((fin) => {
                const finId = (fin as any).id;
                return finId && finId.toString() === targetId.toString();
              });
              if (financialToDelete) {
                // Only mark for deletion, don't remove from array yet (will be processed on Save)
                setDeletedFinancials(prev => {
                  const exists = prev.some(d => d.index === originalIndex);
                  if (exists) return prev;
                  return [...prev, { index: originalIndex, data: financialToDelete }];
                });
              }
            }
          } catch (e) {
            console.error('Error parsing financial delete old_value:', e);
          }
        } else if (type === 'director') {
          try {
            const oldData = changeRequest.old_value ? JSON.parse(changeRequest.old_value) : null;
            const targetId = oldData?.id;
            if (targetId) {
              const directorToDelete = directors.find((dir) => {
                const dirId = (dir as any).id;
                return dirId && dirId.toString() === targetId.toString();
              });
              if (directorToDelete) {
                // Only mark for deletion, don't remove from array yet (will be processed on Save)
                setDeletedDirectors(prev => {
                  const exists = prev.some(d => d.index === originalIndex);
                  if (exists) return prev;
                  return [...prev, { index: originalIndex, data: directorToDelete }];
                });
              }
            }
          } catch (e) {
            console.error('Error parsing director delete old_value:', e);
          }
        } else if (type === 'member') {
          try {
            const oldData = changeRequest.old_value ? JSON.parse(changeRequest.old_value) : null;
            const targetId = oldData?.id;
            if (targetId) {
              const memberToDelete = members.find((mem) => {
                const memId = (mem as any).id;
                return memId && memId.toString() === targetId.toString();
              });
              if (memberToDelete) {
                // Only mark for deletion, don't remove from array yet (will be processed on Save)
                setDeletedMembers(prev => {
                  const exists = prev.some(d => d.index === originalIndex);
                  if (exists) return prev;
                  return [...prev, { index: originalIndex, data: memberToDelete }];
                });
              }
            }
          } catch (e) {
            console.error('Error parsing member delete old_value:', e);
          }
        }
      }
    } else if (fieldName === 'msic_add' || fieldName === 'address_add' || fieldName === 'financial_add' || fieldName === 'director_add' || fieldName === 'member_add') {
      // Handle add requests: add new items to local state
      // Only add if they don't already exist (to avoid duplicates)
      try {
        if (fieldName === 'address_add') {
          // address_add now contains a single address object (not an array)
          const newAddress = JSON.parse(newValue || '{}');
          if (newAddress && newAddress.address1) {
            setAddresses(prev => {
              // Check for duplicates by type+address1+address2
              const existingKeys = new Set(prev.map(addr => `${addr.type || ''}_${addr.address1 || ''}_${addr.address2 || ''}`));
              const key = `${newAddress.type || ''}_${newAddress.address1 || ''}_${newAddress.address2 || ''}`;
              if (!existingKeys.has(key)) {
                return [...prev, newAddress];
              }
              return prev;
            });
          }
        } else if (fieldName === 'msic_add') {
          // msic_add now contains a single MSIC code object (not an array)
          const newMsic = JSON.parse(newValue || '{}');
          if (newMsic && newMsic.code) {
            setMsicCodes(prev => {
              // Check for duplicates by code+description
              const existingKeys = new Set(prev.map(msic => `${msic.code || ''}_${msic.description || ''}`));
              const key = `${newMsic.code || ''}_${newMsic.description || ''}`;
              if (key.trim() && !existingKeys.has(key)) {
                return [...prev, newMsic];
              }
              return prev;
            });
          }
        } else if (fieldName === 'financial_add') {
          // financial_add now contains a single financial object (not an array)
          const newFinancial = JSON.parse(newValue || '{}');
          if (newFinancial && (newFinancial.type || newFinancial.bank || newFinancial.bank_account)) {
            setFinancials(prev => {
              // Check for duplicates by type+bank+account
              const existingKeys = new Set(prev.map(fin => `${fin.type || ''}_${fin.bank || ''}_${fin.bank_account || ''}`));
              const key = `${newFinancial.type || ''}_${newFinancial.bank || ''}_${newFinancial.bank_account || ''}`;
              if (!existingKeys.has(key)) {
                return [...prev, newFinancial];
              }
              return prev;
            });
          }
        } else if (fieldName === 'director_add') {
          // director_add now contains a single director object (not an array)
          const newDirector = JSON.parse(newValue || '{}');
          if (newDirector && (newDirector.name || newDirector.identification_no)) {
            setDirectors(prev => {
              // Check for duplicates by name+identification_no
              const existingKeys = new Set(prev.map(dir => `${dir.name || ''}_${dir.identification_no || ''}`));
              const key = `${newDirector.name || ''}_${newDirector.identification_no || ''}`;
              if (key.trim() && !existingKeys.has(key)) {
                return [...prev, newDirector];
              }
              return prev;
            });
          }
        } else if (fieldName === 'member_add') {
          // member_add now contains a single member object (not an array)
          const newMember = JSON.parse(newValue || '{}');
          if (newMember && (newMember.name || newMember.identification_no)) {
            setMembers(prev => {
              // Check for duplicates by name+identification_no
              const existingKeys = new Set(prev.map(mem => `${mem.name || ''}_${mem.identification_no || ''}`));
              const key = `${newMember.name || ''}_${newMember.identification_no || ''}`;
              if (key.trim() && !existingKeys.has(key)) {
                return [...prev, newMember];
              }
              return prev;
            });
          }
        }
      } catch (e) {
        console.error('Error parsing add request:', e);
      }
    } else if (fieldName) {
      // Handle field modification requests
      const fieldMatch = fieldName.match(/^(msic|address|financial|director|member)_(\d+)_(.+)$/);
      if (fieldMatch) {
        const [, type, indexStr, field] = fieldMatch;
        const index = parseInt(indexStr, 10);
        
        if (type === 'msic') {
          const updated = [...msicCodes];
          if (updated[index]) {
            updated[index] = { ...updated[index], [field]: newValue };
            setMsicCodes(updated);
          }
        } else if (type === 'address') {
          const updated = [...addresses];
          if (updated[index]) {
            updated[index] = { ...updated[index], [field]: newValue };
            setAddresses(updated);
          }
        } else if (type === 'financial') {
          const updated = [...financials];
          if (updated[index]) {
            updated[index] = { ...updated[index], [field]: newValue };
            setFinancials(updated);
          }
        } else if (type === 'director') {
          const updated = [...directors];
          if (updated[index]) {
            updated[index] = { ...updated[index], [field]: newValue };
            setDirectors(updated);
          }
        } else if (type === 'member') {
          const updated = [...members];
          if (updated[index]) {
            updated[index] = { ...updated[index], [field]: newValue };
            setMembers(updated);
      }
        }
      } else if (fieldName.startsWith('employer_')) {
        // Handle employer field changes
        const employerField = fieldName.replace('employer_', '');
        setEmployer(prev => ({ ...prev, [employerField]: newValue }));
      } else {
        // Handle overview field changes
        form.setFieldValue(fieldName as any, newValue);
      }
    }
    
    // Mark for approval (will be processed on Save)
    setPendingApprovals(prev => {
      if (prev.includes(requestId)) return prev;
      return [...prev, requestId];
    });
    setPendingRejections(prev => prev.filter(id => id !== requestId));
    message.info('Change request will be approved when you click Update');
  };

  const handleRejectChangeRequest = (requestId: number) => {
    const changeRequest = changeRequests.find(cr => cr.id === requestId);
    if (!changeRequest) return;
    
    // Immediately update UI to show rejected status
    setChangeRequests(prev => prev.map(cr => 
      cr.id === requestId 
        ? { ...cr, status: 'rejected' as const, reviewed_at: new Date().toISOString() }
        : cr
    ));
    
    // For reject, we don't need to update local state - just mark for rejection
    // The UI will continue to show the original values
    
    // Mark for rejection (will be processed on Save)
    setPendingRejections(prev => {
      if (prev.includes(requestId)) return prev;
      return [...prev, requestId];
    });
    setPendingApprovals(prev => prev.filter(id => id !== requestId));
    message.info('Change request will be rejected when you click Update');
  };

  const handleApproveAllChangeRequests = async () => {
    if (!id) return;
    if (!hasPendingAnyRequests) {
      message.info('No pending change requests');
      return;
    }
    const totalPending = pendingChangeRequests.length + pendingBusinessRequestsCount;
    if (!window.confirm(`Approve all ${totalPending} pending request(s)?`)) {
      return;
    }
    setVerifying(true);
    try {
      if (pendingBusinessRequestsCount > 0) {
        await businessService.approveAllRequests(parseInt(id, 10));
      }
      for (const request of pendingChangeRequests) {
        await businessService.approveChangeRequest(request.id);
      }
      await loadBusiness(parseInt(id, 10));
      message.success('All pending requests approved');
    } catch {
      message.error('Failed to approve all requests');
    } finally {
      setVerifying(false);
    }
  };

  const handleRejectAllChangeRequests = async () => {
    if (!id) return;
    if (!hasPendingAnyRequests) {
      message.info('No pending change requests');
      return;
    }
    setRejectAllModalOpen(true);
  };

  const handleApprovePendingChange = async (fieldName: string) => {
    try {
      const newValue = pendingChanges[fieldName];
      const oldValue = form.getFieldValue(fieldName as any);
      
      if (!newValue || newValue === oldValue) {
        message.warning('No change to approve');
        return;
      }

      await businessService.createChangeRequest({
        business_id: parseInt(id!, 10),
        field_name: fieldName,
        old_value: oldValue || '',
        new_value: newValue,
        status: 'approved',
      });

      form.setFieldValue(fieldName as any, newValue);
      
      const updated = { ...pendingChanges };
      delete updated[fieldName];
      setPendingChanges(updated);

      message.success('Change approved and applied');
      
      // Reload business data to refresh UI without full page reload
      if (id) {
        await loadBusiness(parseInt(id, 10));
      }
    } catch {
      message.error('Failed to approve change');
    }
  };

  const handleRejectPendingChange = (fieldName: string) => {
    const updated = { ...pendingChanges };
    delete updated[fieldName];
    setPendingChanges(updated);
    message.info('Change rejected');
  };

  const handleFieldChange = (fieldName: string, value: string) => {
    setPendingChanges(prev => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const getDisplayValue = (fieldName: string): string => {
    if (pendingChanges[fieldName]) {
      return pendingChanges[fieldName];
    }
    const changeRequest = getChangeRequestForField(fieldName);
    if (changeRequest) {
      return changeRequest.new_value;
    }
    return '';
  };

  const handleTypeChange = async (newType: 'potential' | 'active' | 'inactive') => {
    setBusinessType(newType);
    form.setFieldValue('type', newType);
    
    // If in edit mode, save immediately (like CustomerForm)
    if (isEdit && id) {
      try {
        // Ensure required fields are included
        const formValues = form.getFieldsValue();
        const updateData: any = { type: newType };
        
        // Include required fields if they exist
        if (formValues.customer_id) {
          updateData.customer_id = formValues.customer_id;
        }
        if (formValues.name) {
          updateData.name = formValues.name;
        }
        
        await businessService.update(parseInt(id, 10), updateData);
        message.success('Business type updated');
        
        // Reload business data to refresh UI
        await loadBusiness(parseInt(id, 10));
      } catch (error: any) {
        console.error('Failed to update business type:', error);
        message.error(error.response?.data?.message || 'Failed to update business type');
        // Revert on error
        const currentType = form.getFieldValue('type') || businessType || 'potential';
        setBusinessType(currentType);
        form.setFieldValue('type', currentType);
      }
    }
  };

  const handleSupportingFileChange = (field: string, files: UploadFile[]) => {
    setSupportingFiles((prev) => {
      const prevFiles = prev[field as keyof typeof prev] || [];
      const hadExisting = prevFiles.length > 0 && !prevFiles[0].originFileObj;

      setRemovedSupportingFiles((removedPrev) => {
        if (files.length === 0 && hadExisting) {
          return { ...removedPrev, [field]: true };
        }
        if (files.length > 0 && removedPrev[field]) {
          const { [field]: _removed, ...rest } = removedPrev;
          return rest;
        }
        return removedPrev;
      });

      return {
        ...prev,
        [field]: files,
      };
    });
  };

  return (
    <Spin spinning={loadingBusiness} size="large">
    <div>
      <Modal
        title="Reject all pending requests?"
        open={rejectAllModalOpen}
        okText="Reject"
        okButtonProps={{ danger: true, loading: verifying }}
        cancelText="Cancel"
        onCancel={() => setRejectAllModalOpen(false)}
        onOk={async () => {
          if (!id) {
            setRejectAllModalOpen(false);
            return;
          }
          setVerifying(true);
          try {
            if (pendingBusinessRequestsCount > 0) {
              await businessService.rejectAllRequests(parseInt(id, 10));
            }
            for (const request of pendingChangeRequests) {
              await businessService.rejectChangeRequest(request.id);
            }
            await loadBusiness(parseInt(id, 10));
            message.success('All pending requests rejected');
          } catch {
            message.error('Failed to reject all requests');
          } finally {
            setVerifying(false);
            setRejectAllModalOpen(false);
          }
        }}
      >
        <p>This will discard all pending changes for this business.</p>
      </Modal>
      {/* Customer Portal: Business Info Header */}
      {isCustomerPortalPage && isEdit && businessName && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
            paddingBottom: 16,
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <Title level={2} style={{ margin: 0, color: '#1890ff', fontSize: 28, fontWeight: 600 }}>
            {businessName}
          </Title>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/customer/business')}
            >
              Back
            </Button>
            {!isEditing && canEnterEditMode && (
              <Button
                onClick={() => {
                  setIsEditing(true);
                  const nextTab = getDefaultTabKey();
                  setActiveTab(nextTab);
                  setSearchParams({ tab: nextTab });
                }}
              >
                Edit
              </Button>
            )}
          </Space>
        </div>
      )}

    <Card bordered={false} bodyStyle={{ padding: 0 }} style={{ boxShadow: 'none' }}>
        {/* Admin Portal: Original Header */}
        {!isCustomerPortalPage && isEdit && businessName && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: '1px solid #f0f0f0',
        }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff', fontSize: 28, fontWeight: 600 }}>
            {businessName}
          </Title>
            <Space>
              {isEditing && canVerifyRequests && hasPendingAnyRequests && (
                <>
                  <Button type="primary" onClick={handleApproveAllChangeRequests} loading={verifying}>
                    Approve
                  </Button>
                  <Button danger onClick={handleRejectAllChangeRequests} loading={verifying}>
                    Reject
                  </Button>
                </>
              )}
              {!isEditing && canEnterEditMode && (
                <Button
                  onClick={() => {
                    setIsEditing(true);
                    const nextTab = getDefaultTabKey();
                    setActiveTab(nextTab);
                    setSearchParams({ tab: nextTab });
                  }}
                >
                  Edit
                </Button>
              )}
          {isEdit && isEditing && canDelete && (
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              Delete
            </Button>
          )}
            </Space>
        </div>
      )}
      
      {!isEdit && (
      <Title level={3} style={{ marginBottom: 24 }}>
          New Business
      </Title>
      )}
      {submitNotice && (
        <div style={{ marginBottom: 16 }}>
          <Alert color={submitNotice.type} onDismiss={() => setSubmitNotice(null)}>
            {submitNotice.message}
          </Alert>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFinishFailed={(errorInfo) => {
          message.error('Please fix the form errors before submitting');
        }}
        initialValues={{ type: 'potential' }}
        disabled={loadingBusiness}
        onValuesChange={(changedValues, allValues) => {
          // Update business name when name field changes
          if (changedValues.name !== undefined) {
            setBusinessName(changedValues.name);
          }
          // Update business type when type field changes
          if (changedValues.type !== undefined) {
            setBusinessType(changedValues.type);
          }
        }}
      >
        <Tabs 
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            setSearchParams({ tab: key });
          }}
          tabBarExtraContent={
            isEdit ? (
              <Space size={8}>
                <div
                  style={{
                    padding: '4px 12px',
                    borderRadius: '12px',
                    backgroundColor:
                      businessType === 'potential'
                        ? '#FFF7E6'
                        : businessType === 'active'
                          ? '#E6F7FF'
                          : '#FFF1F0',
                    border:
                      businessType === 'potential'
                        ? '1px solid #FFA940'
                        : businessType === 'active'
                          ? '1px solid #40A9FF'
                          : '1px solid #FF7875',
                    color:
                      businessType === 'potential'
                        ? '#FF8C00'
                        : businessType === 'active'
                          ? '#1890ff'
                          : '#ff4d4f',
                    fontWeight: 500,
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor:
                        businessType === 'potential'
                          ? '#FF8C00'
                          : businessType === 'active'
                            ? '#1890ff'
                            : '#ff4d4f',
                    }}
                  ></span>
                  {businessType === 'potential'
                    ? 'Potential'
                    : businessType === 'active'
                      ? 'Active'
                      : 'Inactive'}
                </div>
              </Space>
            ) : null
          }
        >
          {canSeeDetailsTab && (
            <TabPane tab="Overview" key="overview">
              <BusinessOverviewTab
                form={form}
                isEdit={isEdit}
                isEditing={isEditing && canEditDetailsFields}
                canViewCompanyChop={canSeeCompanyChop}
                canEditCompanyChop={canEditCompanyChopFields}
                canDeleteCompanyChop={canEditCompanyChopFields}
                isCustomerPortal={isCustomerPortalPage}
                customers={customers}
                getDisplayValue={getDisplayValue}
                handleFieldChange={handleFieldChange}
                getChangeRequestForField={getChangeRequestForField}
                handleApproveChangeRequest={handleApproveChangeRequest}
                handleRejectChangeRequest={handleRejectChangeRequest}
                handleApprovePendingChange={handleApprovePendingChange}
                handleRejectPendingChange={handleRejectPendingChange}
                supportingFiles={supportingFiles}
                onFileChange={handleSupportingFileChange}
                businessId={isEdit && id ? parseInt(id, 10) : undefined}
                latestRequest={latestBusinessRequest}
              />
            </TabPane>
          )}

          {canSeeEngagementTab && (
            <TabPane tab="Engagement" key="engagement">
              <BusinessEngagementTab
                form={form}
                staff={staff}
                isEditing={isEditing && canEditEngagementFields}
                isCustomerPortal={isCustomerPortalPage}
                onEngagementDateChange={handleEngagementDateChange}
                engagementFiles={engagementFiles}
                onFileChange={(field, files) => {
                  setEngagementFiles(prev => ({
                    ...prev,
                    [field]: files,
                  }));
                }}
                businessId={isEdit && id ? parseInt(id, 10) : undefined}
                latestRequest={latestBusinessEngagementRequest}
              />
            </TabPane>
          )}

          {canSeeAddressTab && (
            <TabPane tab="Address" key="address">
              <BusinessAddressTab
                isEdit={isEdit}
                isEditing={isEditing && canEditAddressFields}
                isCustomerPortal={isCustomerPortalPage}
                addresses={addresses}
                deletedAddresses={deletedAddresses}
                changeRequests={changeRequests}
                addAddress={addAddress}
                removeAddress={markAddressForDeletion}
                updateAddress={updateAddress}
                getAddressDisplayValue={getAddressDisplayValue}
                handleAddressFieldChange={handleAddressFieldChange}
                handleApproveChangeRequest={handleApproveChangeRequest}
                handleRejectChangeRequest={handleRejectChangeRequest}
                addressFiles={addressFiles}
                onAddressFileChange={(index, file) => {
                  const fileKey = `address_${index}`;
                  setAddressFiles(prev => ({
                    ...prev,
                    [fileKey]: file ? [file] : [],
                  }));
                }}
                businessId={isEdit && id ? parseInt(id, 10) : undefined}
              />
            </TabPane>
          )}

          {canSeeNatureTab && (
            <TabPane tab="Nature" key="nature">
              <BusinessNatureTab
                isEdit={isEdit}
                isEditing={isEditing && canEditNatureFields}
                isCustomerPortal={isCustomerPortalPage}
                form={form}
                msicCodes={msicCodes}
                deletedMsicCodes={deletedMsicCodes}
                changeRequests={changeRequests}
                supportingFiles={supportingFiles}
                onFileChange={handleSupportingFileChange}
                businessId={isEdit && id ? parseInt(id, 10) : undefined}
                latestRequest={latestBusinessNatureRequest}
                addMsicCode={addMsicCode}
                removeMsicCode={removeMsicCode}
                updateMsicCode={updateMsicCode}
                getMsicCodeDisplayValue={getMsicCodeDisplayValue}
                handleMsicCodeFieldChange={handleMsicCodeFieldChange}
                handleApproveChangeRequest={handleApproveChangeRequest}
                handleRejectChangeRequest={handleRejectChangeRequest}
                handleApproveMsicCodeDelete={handleApproveChangeRequest}
                handleRejectMsicCodeDelete={handleRejectChangeRequest}
              />
            </TabPane>
          )}

          {canSeeFinancialTab && (
            <TabPane tab="Financial" key="financial">
              <BusinessFinancialTab
                isEdit={isEdit}
                isEditing={isEditing && canEditFinancialFields}
                isCustomerPortal={isCustomerPortalPage}
                financials={financials}
                deletedFinancials={deletedFinancials}
                changeRequests={changeRequests}
                addFinancial={addFinancial}
                removeFinancial={removeFinancial}
                updateFinancial={updateFinancial}
                getFinancialDisplayValue={getFinancialDisplayValue}
                handleFinancialFieldChange={handleFinancialFieldChange}
                handleApproveChangeRequest={handleApproveChangeRequest}
                handleRejectChangeRequest={handleRejectChangeRequest}
                handleApproveFinancialDelete={handleApproveChangeRequest}
                handleRejectFinancialDelete={handleRejectChangeRequest}
              />
            </TabPane>
          )}

          {canSeeEmployersTab && (
            <TabPane tab="Employers" key="employers">
              <BusinessEmployersTab
                employer={employer}
                updateEmployer={updateEmployer}
                isEdit={isEdit}
                isEditing={isEditing && canEditEmployersFields}
                isCustomerPortal={isCustomerPortalPage}
                changeRequests={changeRequests}
                latestRequest={latestBusinessEmployerRequest}
                getEmployerDisplayValue={getEmployerDisplayValue}
                handleEmployerFieldChange={handleEmployerFieldChange}
                handleApproveChangeRequest={handleApproveChangeRequest}
                handleRejectChangeRequest={handleRejectChangeRequest}
              />
            </TabPane>
          )}

          {canSeeDirectorsTab && (
            <TabPane tab="Directors" key="directors">
              <BusinessDirectorsTab
                directors={directors}
                addDirector={addDirector}
                removeDirector={removeDirector}
                updateDirector={updateDirector}
                isEdit={isEdit}
                isEditing={isEditing && canEditDirectorsFields}
                isCustomerPortal={isCustomerPortalPage}
                deletedDirectors={deletedDirectors}
                changeRequests={changeRequests}
                getDirectorDisplayValue={getDirectorDisplayValue}
                handleDirectorFieldChange={handleDirectorFieldChange}
                handleApproveChangeRequest={handleApproveChangeRequest}
                handleRejectChangeRequest={handleRejectChangeRequest}
                handleApproveDirectorDelete={handleApproveChangeRequest}
                handleRejectDirectorDelete={handleRejectChangeRequest}
                customers={customers}
              />
            </TabPane>
          )}

          {canSeeMembersTab && (
            <TabPane tab="Members" key="members">
              <BusinessMembersTab
                members={members}
                addMember={addMember}
                removeMember={removeMember}
                updateMember={updateMember}
                isEdit={isEdit}
                isEditing={isEditing && canEditMembersFields}
                isCustomerPortal={isCustomerPortalPage}
                deletedMembers={deletedMembers}
                changeRequests={changeRequests}
                getMemberDisplayValue={getMemberDisplayValue}
                handleMemberFieldChange={handleMemberFieldChange}
                handleApproveChangeRequest={handleApproveChangeRequest}
                handleRejectChangeRequest={handleRejectChangeRequest}
                handleApproveMemberDelete={handleApproveChangeRequest}
                handleRejectMemberDelete={handleRejectChangeRequest}
                customers={customers}
                businesses={businesses}
              />
            </TabPane>
          )}


          {canSeeTimelineTab && (
            <TabPane tab="Timeline" key="timeline">
              <BusinessTimelineTab
                changeRequests={changeRequests}
              />
            </TabPane>
          )}
        </Tabs>
      </Form>

      <div style={{ 
        padding: '16px 24px', 
        borderTop: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: 24,
        position: 'sticky',
        bottom: 0,
        backgroundColor: '#fff',
        zIndex: 10,
        
      }}>
        <Space>
          {isEdit ? (
            <>
              {isEditing ? (
                <>
                  <Button
                    onClick={() => {
                      setIsEditing(false);
                      if (isEdit && id) {
                        loadBusiness(parseInt(id, 10));
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="primary" onClick={() => form.submit()} loading={submitting}>
                    Save
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={() => navigate(isCustomerPortalPage ? '/customer/personal' : '/businesses')}>
                    Cancel
                  </Button>
                  {(pendingApprovals.length > 0 || pendingRejections.length > 0) ? (
                    <Button 
                      type="primary" 
                      onClick={() => form.submit()} 
                      loading={submitting}
                    >
                      Update
                    </Button>
                  ) : null}
                </>
              )}
            </>
          ) : (
            <>
              <Button onClick={() => navigate(isCustomerPortalPage ? '/customer/personal' : '/businesses')}>Cancel</Button>
          <Button type="primary" onClick={() => form.submit()} loading={submitting}>
                Create
          </Button>
            </>
          )}
        </Space>
      </div>
    </Card>
    </div>
    </Spin>
  );
};
